import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sanitizeString, sanitizeSearchInput } from '../utils/security';
import { searchCustomers, createCustomer, fetchCustomerDetail } from '../services/customersService';
import { fetchDevicesByCustomer } from '../services/devicesService';
import { fetchAddressesByCustomer, createAddress } from '../services/addressesService';
import { fetchCities, fetchDistrictsByCity } from '../services/locationsService';
import { createServiceRecord, fetchServiceRecords, fetchServisKaydiHub, createTaslakServisKaydi, patchTaslakServisKaydi, tamamlaTaslakServisKaydi, fetchServiceRecordDetail } from '../services/serviceRecordsService';
import { fetchCategoryRoot } from '../services/categoriesService';
import { fetchFaultRecordsTree } from '../services/faultRecordsService';
import { fetchTechServices, searchTechServices } from '../services/techServicesService';
import { FoldedSearchHighlight } from '../components/FoldedSearchHighlight';
import {
  foldedSearchTokens,
  productMatchesFoldedTokens,
  buildProductCategoryTree,
  annotateProductCategoryAcc,
} from '../utils/productPickerCore';
import { fetchAuthorizedTechServices } from '../services/serviceAreasService';
import { createGorusmeKaydi, updateGorusmeKaydi, fetchGorusmeKayitlari } from '../services/gorusmeKaydiService';
import { getCustomerSms } from '../services/smsService';
import { formatTelefonGoster, normalizeTurkishPhone } from '../utils/phoneNormalize';
import { musteriIletisimIzniVerildi } from '../utils/musteriIletisimIzni';
import { fetchAllContentsByPaginating } from '../services/productsService';
import { fetchCategories } from '../services/categoriesService';
import { fetchChecklistSablonByUrun } from '../services/checklistsService';
import { fetchBakimFiyatOnizleme } from '../services/bakimFiyatService';
import TeknikServisInfoModal from '../components/TeknikServisInfoModal';
import MusteriServisKaydiTabModal from '../components/MusteriServisKaydiTabModal';
import GorusmeHubStep from '../components/GorusmeHubStep';
import MusteriIzinDogrulamaModal from '../components/MusteriIzinDogrulamaModal';
import { useSoftphoneOptional } from '../contexts/SoftphoneContext';
import { useSantralGorusmeOptional } from '../contexts/SantralGorusmeContext';
import { clearSantralWizardSnapshot } from '../utils/santralWizardStorage';
import { clearOnServisWizardSnapshot } from '../utils/onServisWizardStorage';
import { clearManualWizardSnapshot } from '../utils/manualWizardStorage';
import { clearServiceRecordCreateCaches } from '../utils/serviceRecordCreateNavigation';
import { useServiceRecordWizardDraftOptional } from '../contexts/ServiceRecordWizardDraftContext';
import { buildGeciciTaslakSnapshot } from '../utils/serviceRecordWizardState';
import { applyGeciciTaslakSnapshot, applyKaliciTaslakRecord, buildKaliciTaslakPayload } from '../utils/serviceRecordWizardRestore';
import { fetchOnServisKaydiDetay } from '../services/onServisKaydiService';
import '../components/GorusmeHubStep.css';

const TABS = [
  { key: 'customer-search', label: 'Müşteri Arama', step: 1 },
  { key: 'customer-create', label: 'Müşteri Oluştur', step: 2 },
  { key: 'gorusme-hub', label: 'Santral', step: 'S' },
  { key: 'operation-select', label: 'İşlem Seç', step: 3 },
  { key: 'product-select', label: 'Ürün Seç', step: 4, conditional: 'Kurulum' },
  { key: 'checklist-questions', label: 'Kontrol Listesi', step: 5, conditional: 'Kurulum' },
  { key: 'device-select', label: 'Cihaz Seç', step: 6 },
  { key: 'checklist-after-device', label: 'Kontrol Listesi', step: 6.5, conditional: 'ArızaBakım' },
  { key: 'fault-select', label: 'Arıza Seç', step: 7, conditional: true }, // conditional: true => Arıza
  { key: 'address-select', label: 'Adres Seç', step: 8 },
  { key: 'tech-service-select', label: 'Teknik Servis Seç', step: 9 },
  // İstek ve Şikayet alt-akışı (yalnızca islemTipi === 'İstek ve Şikayet' iken görünür)
  { key: 'complaint-category-select', label: 'Kategori Seç', step: 4, conditional: 'İstek ve Şikayet' },
  { key: 'complaint-product-select', label: 'Ürün Seç', step: 5, conditional: 'İstek ve Şikayet/Ürün' },
  { key: 'complaint-tech-service-select', label: 'Teknik Servis', step: 5, conditional: 'İstek ve Şikayet/Teknik Servis' },
  { key: 'complaint-note', label: 'Not', step: 6, conditional: 'İstek ve Şikayet' },
  { key: 'finish', label: 'Bitir', step: 10 },
];

const OPERATION_TYPES = [
  { value: 'Kurulum', label: 'Kurulum', icon: 'fa-wrench' },
  { value: 'Bakım', label: 'Bakım', icon: 'fa-tools' },
  { value: 'Arıza', label: 'Arıza', icon: 'fa-exclamation-triangle' },
  { value: 'İstek ve Şikayet', label: 'İstek ve Şikayet', icon: 'fa-comment-dots' },
];

const COMPLAINT_CATEGORIES = [
  { value: 'Ürün', label: 'Ürün', icon: 'fa-box' },
  { value: 'Bayi', label: 'Bayi', icon: 'fa-store' },
  { value: 'Teknik Servis', label: 'Teknik Servis', icon: 'fa-wrench' },
  { value: 'Müşteri Hizmetleri', label: 'Müşteri Hizmetleri', icon: 'fa-headset' },
  { value: 'Diğer', label: 'Diğer', icon: 'fa-ellipsis-h' },
];

const PRIORITY_LEVELS = [
  { value: 'Acil', label: 'Acil' },
  { value: 'Yüksek', label: 'Yüksek' },
  { value: 'Orta', label: 'Orta' },
  { value: 'Düşük', label: 'Düşük' },
];

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const ServiceRecordCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const softphone = useSoftphoneOptional();
  const hangupCall = softphone?.hangupCall;
  const santralGorusme = useSantralGorusmeOptional();
  const wizardDraft = useServiceRecordWizardDraftOptional();
  const updateSantralSession = santralGorusme?.updateSession;
  const clearSantralSession = santralGorusme?.clearSession;
  const suppressCustomerSearchRef = useRef(false);
  const lastSantralGorusmeRef = useRef(null);
  const onServisInitRef = useRef(null);
  const restoreAppliedRef = useRef(false);
  const kaliciTaslakInitRef = useRef(null);
  const [kaliciTaslakId, setKaliciTaslakId] = useState(null);
  const [kaliciKaydetLoading, setKaliciKaydetLoading] = useState(false);
  const [onServisKaydiId, setOnServisKaydiId] = useState(null);

  // Tab yönetimi
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  
  // Form state'leri
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Sekme kilitleme - hangi sekmeye kadar gidilebileceğini tutar
  const [maxAllowedTab, setMaxAllowedTab] = useState('customer-search');
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  
  // Müşteri oluşturma state'leri
  const [newCustomerForm, setNewCustomerForm] = useState({
    adi: '',
    soyadi: '',
    telefon: '',
    mail: '',
    adres: '',
    cityId: '',
    districtId: '',
  });
  const [newCustomerError, setNewCustomerError] = useState('');
  const [newCustomerSubmitting, setNewCustomerSubmitting] = useState(false);
  const [phonePrefilledFromSearch, setPhonePrefilledFromSearch] = useState(false);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [illerLoading, setIllerLoading] = useState(false);
  const [ilcelerLoading, setIlcelerLoading] = useState(false);
  
  // İşlem seçimi
  const [selectedOperation, setSelectedOperation] = useState('');
  const [odemeBeyaniChecked, setOdemeBeyaniChecked] = useState(false);
  
  // Arıza seçimi (sadece Arıza seçildiğinde kullanılacak)
  const [faultCategories, setFaultCategories] = useState([]);
  const [faultRecords, setFaultRecords] = useState([]);
  const [selectedFaults, setSelectedFaults] = useState([]);
  const [faultNote, setFaultNote] = useState('');
  const [isFaultSearchModalOpen, setIsFaultSearchModalOpen] = useState(false);
  const [modalSelectedFaults, setModalSelectedFaults] = useState([]); // Modal içindeki seçimler
  const [faultAccordionOpenIds, setFaultAccordionOpenIds] = useState({});
  const [faultsLoading, setFaultsLoading] = useState(false);
  
  // Cihaz seçimi
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [deviceNotSelected, setDeviceNotSelected] = useState(false);
  const [deviceExplanation, setDeviceExplanation] = useState('');
  const [devicesLoading, setDevicesLoading] = useState(false);
  
  // Adres seçimi
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [newAddressText, setNewAddressText] = useState('');
  const [newAddressCityId, setNewAddressCityId] = useState('');
  const [newAddressDistrictId, setNewAddressDistrictId] = useState('');
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  // Adres ekleme için il/ilçe state'leri
  const [serviceAddressIller, setServiceAddressIller] = useState([]);
  const [serviceAddressIlceler, setServiceAddressIlceler] = useState([]);
  const [serviceAddressIllerLoading, setServiceAddressIllerLoading] = useState(false);
  const [serviceAddressIlcelerLoading, setServiceAddressIlcelerLoading] = useState(false);
  
  // Teknik servis seçimi (array olarak)
  const [techServices, setTechServices] = useState([]);
  const [selectedTechServices, setSelectedTechServices] = useState([]); // En fazla 1 teknik servis
  const [techServicesLoading, setTechServicesLoading] = useState(false);
  const [techServiceSearchInput, setTechServiceSearchInput] = useState('');
  const [techServiceSearchResults, setTechServiceSearchResults] = useState([]);
  const [techServiceSearchLoading, setTechServiceSearchLoading] = useState(false);
  
  // Bitir tab'ı
  const [priority, setPriority] = useState('Orta');
  const [notes, setNotes] = useState('');
  const [otpVarsayilanNumara, setOtpVarsayilanNumara] = useState(true);
  const [otpAlternatifTelefon, setOtpAlternatifTelefon] = useState('');
  const [finalSubmitting, setFinalSubmitting] = useState(false);
  const [bakimFiyatOnizleme, setBakimFiyatOnizleme] = useState(null);
  const [bakimFiyatYukleniyor, setBakimFiyatYukleniyor] = useState(false);

  // İstek ve Şikayet alt-akışı
  const [complaintCategory, setComplaintCategory] = useState('');
  const [complaintTargetIcerikId, setComplaintTargetIcerikId] = useState('');
  const [complaintTargetCihazId, setComplaintTargetCihazId] = useState('');
  const [complaintTargetTechServiceId, setComplaintTargetTechServiceId] = useState('');
  const [complaintTargetServisKaydiId, setComplaintTargetServisKaydiId] = useState('');
  const [complaintNote, setComplaintNote] = useState('');
  /** İstek ve Şikayet kayıtlarında: İstek mi şikâyet mi */
  const [istekSikayetTuru, setIstekSikayetTuru] = useState('');
  const [complaintCityId, setComplaintCityId] = useState('');
  const [complaintTsSearchInput, setComplaintTsSearchInput] = useState('');
  const [complaintTsResults, setComplaintTsResults] = useState([]);
  const [complaintTsLoading, setComplaintTsLoading] = useState(false);
  const [complaintMusteriServisKayitlari, setComplaintMusteriServisKayitlari] = useState([]);
  const [complaintMusteriServisKayitlariLoading, setComplaintMusteriServisKayitlariLoading] = useState(false);
  const [openTechServiceInfoId, setOpenTechServiceInfoId] = useState(null);
  const [openMusteriServisKaydiId, setOpenMusteriServisKaydiId] = useState(null);

  // Görüşme kaydı (müşteri seçildiğinde veya müşteri oluştur tıklandığında)
  const [gorusmeKaydiId, setGorusmeKaydiId] = useState(null);
  const [santralTelefon, setSantralTelefon] = useState(null);
  const [gorusmeSonlandirLoading, setGorusmeSonlandirLoading] = useState(false);
  const [gorusmeSonlandirModalOpen, setGorusmeSonlandirModalOpen] = useState(false);
  const [gorusmeSonlandirForm, setGorusmeSonlandirForm] = useState({
    telefon: '',
    sonlanmaSebebi: '',
    notlar: '',
  });

  const [isSantralFlow, setIsSantralFlow] = useState(false);
  const [hubAcikKayitlar, setHubAcikKayitlar] = useState([]);
  const [hubGecmisKayitlar, setHubGecmisKayitlar] = useState([]);
  const [hubMusteriDetay, setHubMusteriDetay] = useState(null);
  const [hubAdresler, setHubAdresler] = useState([]);
  const [hubCihazlar, setHubCihazlar] = useState([]);
  const [hubGorusmeler, setHubGorusmeler] = useState([]);
  const [hubSms, setHubSms] = useState([]);
  const [hubGorusmelerLoaded, setHubGorusmelerLoaded] = useState(false);
  const [hubSmsLoaded, setHubSmsLoaded] = useState(false);
  const [hubGorusmelerLoading, setHubGorusmelerLoading] = useState(false);
  const [hubSmsLoading, setHubSmsLoading] = useState(false);
  const [hubYukleniyor, setHubYukleniyor] = useState(false);
  const [izinModalOpen, setIzinModalOpen] = useState(false);

  // Kurulum: ürün seçimi ve gereksinim soruları
  const [kurulumUrunler, setKurulumUrunler] = useState([]);
  const [kurulumUrunkategorileri, setKurulumUrunkategorileri] = useState([]);
  const [kurulumUrunlerLoading, setKurulumUrunlerLoading] = useState(false);
  const [kurulumUrunArama, setKurulumUrunArama] = useState('');
  const [kurulumUrunAramaDebounced, setKurulumUrunAramaDebounced] = useState('');
  const [kurulumAccordionOpenIds, setKurulumAccordionOpenIds] = useState({});
  const [kurulumIcerikId, setKurulumIcerikId] = useState('');
  const [kurulumSeriNo, setKurulumSeriNo] = useState('');
  const [checklistSablon, setChecklistSablon] = useState(null);
  const [checklistSablonLoading, setChecklistSablonLoading] = useState(false);
  const [checklistCevaplari, setChecklistCevaplari] = useState({}); // { soruId: 'evet' | 'hayir' }
  const [checklistDegerleri, setChecklistDegerleri] = useState({}); // { soruId: string }

  const cagriMerkeziSorular = useMemo(() => {
    if (!checklistSablon?.sorular?.length) return [];
    return checklistSablon.sorular.filter((s) => s.cagriMerkezi === true && s.pasif !== true);
  }, [checklistSablon]);

  const resolveChecklistUrunIcerikId = useCallback(() => {
    if (selectedOperation === 'Kurulum') {
      return kurulumIcerikId ? String(kurulumIcerikId) : '';
    }
    if (selectedOperation === 'Arıza' || selectedOperation === 'Bakım') {
      if (deviceNotSelected || !selectedDevices.length) return '';
      const deviceId = selectedDevices[0];
      const device = devices.find((d) => String(d.id || d._id) === String(deviceId));
      if (!device) return '';
      const icerikId = device.icerikId || device.urun?.id || device.urun?._id;
      return icerikId ? String(icerikId) : '';
    }
    return '';
  }, [selectedOperation, kurulumIcerikId, deviceNotSelected, selectedDevices, devices]);

  const buildChecklistTelefonPayload = useCallback((sablon = checklistSablon) => {
    const checklistId = sablon?._id || sablon?.id;
    const urunId = resolveChecklistUrunIcerikId();
    const checklistOps = ['Kurulum', 'Arıza', 'Bakım'];
    if (!checklistOps.includes(selectedOperation) || !urunId || !checklistId) return undefined;
    const sorular = (sablon.sorular || []).filter((s) => s.cagriMerkezi === true && s.pasif !== true);
    if (sorular.length === 0) return undefined;
    const cevaplar = sorular.map((s) => {
      const sid = String(s._id || s.id);
      const cevap = checklistCevaplari[sid];
      if (cevap !== 'evet' && cevap !== 'hayir') return null;
      return {
        soruId: sid,
        soruMetni: s.metin || '',
        cevap,
        deger: (checklistDegerleri[sid] || '').trim(),
      };
    }).filter(Boolean);
    if (cevaplar.length !== sorular.length) return undefined;
    return {
      checklistId,
      checklistAd: sablon.ad || '',
      cevaplar,
    };
  }, [selectedOperation, resolveChecklistUrunIcerikId, checklistSablon, checklistCevaplari, checklistDegerleri]);

  // Ürün + işlem tipine göre checklist şablonunu getir
  useEffect(() => {
    const urunId = resolveChecklistUrunIcerikId();
    const checklistOps = ['Kurulum', 'Arıza', 'Bakım'];
    if (!checklistOps.includes(selectedOperation) || !urunId) {
      if (!urunId) setChecklistSablon(null);
      return undefined;
    }
    let cancelled = false;
    setChecklistSablonLoading(true);
    fetchChecklistSablonByUrun(urunId, { islemTipi: selectedOperation })
      .then((res) => {
        if (cancelled) return;
        setChecklistSablon(res?.veri?.checklistSablon || null);
      })
      .catch(() => {
        if (!cancelled) setChecklistSablon(null);
      })
      .finally(() => {
        if (!cancelled) setChecklistSablonLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedOperation, resolveChecklistUrunIcerikId]);
  
  // Kurulum: arama debounce (ürün adı / kodu — TR + çok kelime)
  useEffect(() => {
    const t = setTimeout(() => {
      setKurulumUrunAramaDebounced(kurulumUrunArama.trim().slice(0, 100));
    }, 300);
    return () => clearTimeout(t);
  }, [kurulumUrunArama]);

  // Kurulum ürün + kategori ağacını yükle (Kurulum ya da İstek/Şikayet→Ürün seçildiğinde)
  useEffect(() => {
    const needTree =
      selectedOperation === 'Kurulum' ||
      (selectedOperation === 'İstek ve Şikayet' && complaintCategory === 'Ürün');
    if (!needTree) return;
    setKurulumUrunlerLoading(true);
    setKurulumUrunArama('');
    setKurulumUrunAramaDebounced('');
    setKurulumAccordionOpenIds({});
    Promise.all([
      fetchCategories({ kategoriTipi: 'urun' }),
      fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true }),
    ])
      .then(([catRes, urunler]) => {
        const cats = catRes?.veri?.kategoriler || [];
        setKurulumUrunkategorileri(Array.isArray(cats) ? cats : []);
        setKurulumUrunler(Array.isArray(urunler) ? urunler : []);
      })
      .catch(() => {
        setKurulumUrunkategorileri([]);
        setKurulumUrunler([]);
      })
      .finally(() => setKurulumUrunlerLoading(false));
  }, [selectedOperation, complaintCategory]);

  const kurulumKategoriYoluMap = useMemo(() => {
    const cats = kurulumUrunkategorileri;
    const byId = new Map(
      cats.map((c) => [String(c.id || c._id), c])
    );
    const pathFor = (kid) => {
      if (!kid) return 'Kategorisiz';
      const parts = [];
      let cur = byId.get(String(kid));
      let guard = 0;
      while (cur && guard < 64) {
        guard += 1;
        parts.unshift(safeText(cur.kategoriAdi));
        const pid = cur.ustKategoriId;
        if (!pid) break;
        cur = byId.get(String(pid));
      }
      return parts.length ? parts.join(' › ').trim() : 'Kategorisiz';
    };
    return { byId, pathFor };
  }, [kurulumUrunkategorileri]);

  const kurulumUrunlerFiltreli = useMemo(() => {
    const tokens = foldedSearchTokens(kurulumUrunAramaDebounced);
    let list = kurulumUrunler;
    if (tokens.length) {
      list = list.filter((u) => productMatchesFoldedTokens(u, tokens));
    }
    list = [...list];
    list.sort((a, b) => {
      const pa = kurulumKategoriYoluMap.pathFor(a.kategoriId);
      const pb = kurulumKategoriYoluMap.pathFor(b.kategoriId);
      const c = pa.localeCompare(pb, 'tr', { sensitivity: 'base' });
      if (c !== 0) return c;
      const na = String(a.icerikAdi || a.urunAdi || '');
      const nb = String(b.icerikAdi || b.urunAdi || '');
      return na.localeCompare(nb, 'tr', { sensitivity: 'base' });
    });
    return list;
  }, [kurulumUrunler, kurulumUrunAramaDebounced, kurulumKategoriYoluMap]);

  const kurulumAccTree = useMemo(() => {
    const rawRoots = buildProductCategoryTree(kurulumUrunkategorileri);
    const pmap = new Map();
    kurulumUrunlerFiltreli.forEach((u) => {
      const kid = u.kategoriId != null ? String(u.kategoriId) : '';
      if (!pmap.has(kid)) pmap.set(kid, []);
      pmap.get(kid).push(u);
    });
    return rawRoots.map((n) => annotateProductCategoryAcc(n, pmap));
  }, [kurulumUrunkategorileri, kurulumUrunlerFiltreli]);

  const kurulumUrunlerKategorisiz = useMemo(
    () => kurulumUrunlerFiltreli.filter((u) => !u.kategoriId),
    [kurulumUrunlerFiltreli]
  );

  // Kurulum: kök kategori akordeonları varsayılan açık
  useEffect(() => {
    if (selectedOperation !== 'Kurulum' || kurulumUrunkategorileri.length === 0) return;
    const roots = buildProductCategoryTree(kurulumUrunkategorileri);
    setKurulumAccordionOpenIds((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const o = {};
      roots.forEach((r) => {
        o[r.cid] = true;
      });
      return o;
    });
  }, [kurulumUrunkategorileri, selectedOperation]);

  // Arama varken eşleşen ürünlerin üst kategorilerini aç
  useEffect(() => {
    if (!kurulumUrunAramaDebounced.trim()) return;
    const byId = new Map(
      kurulumUrunkategorileri.map((c) => [String(c.id || c._id), c])
    );
    setKurulumAccordionOpenIds((prev) => {
      const next = { ...prev };
      const pullUp = (kid) => {
        let cur = kid ? byId.get(String(kid)) : null;
        while (cur) {
          next[String(cur.id || cur._id)] = true;
          const p = cur.ustKategoriId;
          cur = p ? byId.get(String(p)) : null;
        }
      };
      kurulumUrunlerFiltreli.forEach((u) => {
        if (u.kategoriId) pullUp(u.kategoriId);
      });
      return next;
    });
  }, [kurulumUrunAramaDebounced, kurulumUrunlerFiltreli, kurulumUrunkategorileri]);

  useEffect(() => {
    if (!kurulumIcerikId) return;
    const ok = kurulumUrunler.some(
      (u) => String(u.id || u._id) === String(kurulumIcerikId)
    );
    if (!ok) {
      setKurulumIcerikId('');
      setChecklistSablon(null);
      setChecklistCevaplari({});
    }
  }, [kurulumUrunler, kurulumIcerikId]);

  const toggleKurulumAccordion = (cid) => {
    setKurulumAccordionOpenIds((p) => ({ ...p, [cid]: !p[cid] }));
  };

  const renderKurulumAccNode = (item, depth = 0, opts = {}) => {
    if (!item.visible) return null;
    const selectedId = opts.selectedId !== undefined ? opts.selectedId : kurulumIcerikId;
    const onSelectId = opts.onSelectId || ((id) => kurulumUrunSecimde(String(id)));
    const open = kurulumAccordionOpenIds[item.cid] === true;
    const visibleChildren = item.children.filter((c) => c.visible);
    const hasBody = item.products.length > 0 || visibleChildren.length > 0;

    return (
      <div key={item.cid} style={{ marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => hasBody && toggleKurulumAccordion(item.cid)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            background: open ? '#f1f5f9' : '#ffffff',
            cursor: hasBody ? 'pointer' : 'default',
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '14px',
            color: '#1f2937',
            opacity: hasBody ? 1 : 0.85,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <span
            style={{
              width: 18,
              display: 'inline-flex',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '12px',
            }}
            aria-hidden
          >
            {hasBody ? <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} /> : <span style={{ width: 12 }} />}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>{safeText(item.kategoriAdi || '—')}</span>
          {item.products.length > 0 && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748b',
                flexShrink: 0,
              }}
            >
              {item.products.length} ürün
            </span>
          )}
        </button>
        {open && hasBody && (
          <div
            style={{
              marginTop: 8,
              paddingLeft: depth > 0 ? 14 : 4,
              borderLeft: depth > 0 ? '2px solid #e2e8f0' : 'none',
            }}
          >
            {visibleChildren.map((ch) => renderKurulumAccNode(ch, depth + 1, opts))}
            {item.products.map((u) => {
              const uid = u.id || u._id;
              const sel = String(selectedId) === String(uid);
              const ad = u.icerikAdi || u.urunAdi || '—';
              const kod = u.icerikKodu || u.urunKodu || '';
              return (
                <button
                  key={uid}
                  type="button"
                  onClick={() => onSelectId(uid)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    marginBottom: 8,
                    borderRadius: '8px',
                    border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    background: sel ? '#eff6ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease, background 0.15s ease',
                  }}
                >
                  <div style={{ fontWeight: sel ? 600 : 500, color: '#1f2937', lineHeight: 1.4 }}>
                    <FoldedSearchHighlight text={ad} aramaRaw={kurulumUrunAramaDebounced} />
                  </div>
                  {kod ? (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: 4,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      <FoldedSearchHighlight text={kod} aramaRaw={kurulumUrunAramaDebounced} />
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const kurulumUrunSecimde = (uid) => {
    if (!uid) {
      setKurulumIcerikId('');
      setChecklistSablon(null);
      setChecklistCevaplari({});
      return;
    }
    if (String(kurulumIcerikId) === String(uid)) return;
    setKurulumIcerikId(uid);
    setChecklistCevaplari({});
    setChecklistDegerleri({});
    setChecklistSablonLoading(true);
    fetchChecklistSablonByUrun(uid, { islemTipi: 'Kurulum' })
      .then((res) => setChecklistSablon(res?.veri?.checklistSablon || null))
      .catch(() => setChecklistSablon(null))
      .finally(() => setChecklistSablonLoading(false));
  };

  // Eski sessionStorage snapshot'ları temizle (geçici taslak artık WizardDraftContext'te)
  useEffect(() => {
    clearSantralWizardSnapshot();
    clearManualWizardSnapshot();
    clearOnServisWizardSnapshot();
  }, []);

  // Tarayıcı kapatma / yenileme: görüşme varken uyarı
  useEffect(() => {
    if (!gorusmeKaydiId) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gorusmeKaydiId]);

  // Santral cevap → görüşme kaydı ile gelen init
  useEffect(() => {
    const state = location.state;
    if (!state?.fromSantral) return;
    if (state.gorusmeKaydiId && lastSantralGorusmeRef.current === state.gorusmeKaydiId) return;
    lastSantralGorusmeRef.current = state.gorusmeKaydiId || null;

    const initFromSantral = async () => {
      setSelectedOperation('');
      setIsSantralFlow(true);
      if (state.gorusmeKaydiId) {
        setGorusmeKaydiId(state.gorusmeKaydiId);
      }
      if (state.telefon) {
        setSantralTelefon(state.telefon);
      }

      const hubTab = state.initialTab || (state.musteriId ? 'gorusme-hub' : 'customer-search');
      const sessionCustomer = santralGorusme?.session?.selectedCustomer;
      if (sessionCustomer) {
        setSelectedCustomer(sessionCustomer);
        setActiveTab(hubTab);
        setMaxAllowedTab(hubTab);
      }

      if (state.musteriId) {
        try {
          const res = await fetchCustomerDetail(state.musteriId);
          const m = res?.veri?.musteri || res?.veri;
          if (m) {
            setSelectedCustomer({
              id: state.musteriId,
              _id: state.musteriId,
              adi: m.adi || m.name || '',
              soyadi: m.soyadi || m.surname || '',
              name: m.name || m.adi || '',
              surname: m.surname || m.soyadi || '',
              telefon: m.telefon || m.phone || state.telefon || '',
              phone: m.phone || m.telefon || state.telefon || '',
              odemeEksikSerhi: m.odemeEksikSerhi || null,
              iletisimIzni: m.iletisimIzni || { verildi: false },
            });
            setActiveTab(hubTab);
            setMaxAllowedTab(hubTab);
          }
        } catch (err) {
          console.error('Santral müşteri yüklenemedi:', err);
          if (state.telefon) {
            suppressCustomerSearchRef.current = true;
            setCustomerSearchInput(formatTelefonGoster(state.telefon));
            setActiveTab('customer-search');
            setMaxAllowedTab('customer-search');
          }
        }
      } else if (state.telefon) {
        suppressCustomerSearchRef.current = true;
        setCustomerSearchInput(formatTelefonGoster(state.telefon));
        setActiveTab('customer-search');
        setMaxAllowedTab('customer-search');
      }
    };

    initFromSantral();
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  // Ön servis kaydı → servis kaydı oluşturma (URL)
  useEffect(() => {
    if (location.pathname !== '/service-records/create') return;
    if (location.state?.fromSantral) return;
    if (isSantralFlow) return;

    const params = new URLSearchParams(location.search);
    const onServisId = params.get('onServisKaydiId');
    if (!onServisId) return;
    if (onServisInitRef.current === onServisId) return;

    onServisInitRef.current = onServisId;

    const initFromOnServis = async () => {
      try {
        const res = await fetchOnServisKaydiDetay(onServisId);
        const kayit = res?.veri?.onServisKaydi;
        if (!kayit) return;

        setOnServisKaydiId(onServisId);

        if (kayit.icerikId) {
          setSelectedOperation('Kurulum');
          setKurulumIcerikId(String(kayit.icerikId));
        }
        if (kayit.seriNumarasi) {
          setKurulumSeriNo(String(kayit.seriNumarasi));
        }

        let customer = null;
        if (kayit.telefon) {
          const searchRes = await searchCustomers(kayit.telefon, { aktif: true, limit: 5 });
          const list = searchRes?.veri?.musteriler || searchRes?.veri || [];
          const arr = Array.isArray(list) ? list : [];
          customer = arr.find((m) => {
            const ph = normalizeTurkishPhone(m.telefon || m.phone || '');
            return ph === normalizeTurkishPhone(kayit.telefon);
          }) || arr[0];
        }

        if (customer) {
          const cid = customer.id || customer._id;
          setSelectedCustomer({
            id: cid,
            _id: cid,
            adi: customer.adi || customer.name || kayit.musteriAd,
            soyadi: customer.soyadi || customer.surname || kayit.musteriSoyad,
            name: customer.name || customer.adi || kayit.musteriAd,
            surname: customer.surname || customer.soyadi || kayit.musteriSoyad,
            telefon: customer.telefon || customer.phone || kayit.telefon,
            phone: customer.phone || customer.telefon || kayit.telefon,
            odemeEksikSerhi: customer.odemeEksikSerhi || null,
            iletisimIzni: customer.iletisimIzni || { verildi: false },
          });
          setActiveTab('operation-select');
          setMaxAllowedTab('operation-select');
        } else {
          suppressCustomerSearchRef.current = true;
          setCustomerSearchInput(formatTelefonGoster(kayit.telefon || ''));
          setNewCustomerForm((prev) => ({
            ...prev,
            adi: kayit.musteriAd || '',
            soyadi: kayit.musteriSoyad || '',
            telefon: kayit.telefon || '',
          }));
          setPhonePrefilledFromSearch(true);
          setActiveTab('customer-search');
          setMaxAllowedTab('customer-search');
        }

        navigate('/service-records/create', { replace: true });
      } catch (err) {
        console.error('Ön servis kaydı yüklenemedi:', err);
        window.showNotification?.('error', getErrorMessage(err, 'Ön servis kaydı yüklenemedi'));
      }
    };

    initFromOnServis();
  }, [location.pathname, location.search, location.state?.fromSantral, isSantralFlow, navigate]);

  // Arıza kategorilerini ve kayıtlarını yükle (Arıza seçildiğinde)
  useEffect(() => {
    if (selectedOperation === 'Arıza') {
      setFaultsLoading(true);
      // Tree view endpoint'i ile hem kategorileri hem arıza kayıtlarını yükle
      fetchFaultRecordsTree()
        .then(response => {
          const categories = response?.veri?.kategoriler || [];
          setFaultCategories(Array.isArray(categories) ? categories : []);
          
          // Tree view'dan arıza kayıtlarını çıkar
          const allFaults = [];
          const extractFaults = (nodes) => {
            nodes.forEach(node => {
              if (node.arizaKayitlari && Array.isArray(node.arizaKayitlari)) {
                allFaults.push(...node.arizaKayitlari);
              }
              if (node.altKategoriler && Array.isArray(node.altKategoriler)) {
                extractFaults(node.altKategoriler);
              }
            });
          };
          extractFaults(categories);
          setFaultRecords(allFaults);
        })
        .catch(err => {
          console.error('Arıza kategorileri yüklenemedi:', err);
          setFaultCategories([]);
          setFaultRecords([]);
        })
        .finally(() => {
          setFaultsLoading(false);
        });
    } else {
      setFaultCategories([]);
      setFaultRecords([]);
      setSelectedFaults([]);
      setFaultNote('');
    }
  }, [selectedOperation]);
  
  // Modal açıldığında modal seçimlerini ve akordeon durumunu sıfırla
  useEffect(() => {
    if (isFaultSearchModalOpen) {
      setModalSelectedFaults([]);
      setFaultAccordionOpenIds({});
    }
  }, [isFaultSearchModalOpen]);
  
  // İlleri yükle (müşteri oluşturma için)
  useEffect(() => {
    const loadCities = async () => {
      setIllerLoading(true);
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        setIller([]);
      } finally {
        setIllerLoading(false);
      }
    };
    loadCities();
  }, []);

  // Adres ekleme için illeri yükle
  useEffect(() => {
    const loadCities = async () => {
      setServiceAddressIllerLoading(true);
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setServiceAddressIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        setServiceAddressIller([]);
      } finally {
        setServiceAddressIllerLoading(false);
      }
    };
    loadCities();
  }, []);

  // Adres ekleme için ilçeleri yükle
  useEffect(() => {
    const loadDistricts = async () => {
      if (!newAddressCityId || isNaN(parseInt(newAddressCityId, 10))) {
        setServiceAddressIlceler([]);
        return;
      }
      
      setServiceAddressIlcelerLoading(true);
      try {
        const response = await fetchDistrictsByCity(parseInt(newAddressCityId, 10));
        let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri || [];
        
        // Mongoose document'lerini düz objeye çevir
        const serializedList = list.map((ilce) => {
          const raw = ilce._doc || ilce;
          const id = raw._id ?? raw.id;
          const adi = raw.adi ?? raw.ad ?? raw.name;
          return {
            id,
            name: adi,
            ad: adi,
            cityId: raw.cityId
          };
        });
        
        setServiceAddressIlceler(serializedList);
      } catch (err) {
        console.error('İlçeler yüklenemedi:', err);
        setServiceAddressIlceler([]);
      } finally {
        setServiceAddressIlcelerLoading(false);
      }
    };
    loadDistricts();
  }, [newAddressCityId]);

  // İlçeleri yükle
  useEffect(() => {
    const loadDistricts = async () => {
      if (!newCustomerForm.cityId || newCustomerForm.cityId.trim() === '') {
        setIlceler([]);
        return;
      }
      
      const cityIdNum = parseInt(newCustomerForm.cityId, 10);
      if (isNaN(cityIdNum)) {
        setIlceler([]);
        return;
      }
      
      setIlcelerLoading(true);
      try {
        const response = await fetchDistrictsByCity(cityIdNum);
        let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri || [];
        if (!Array.isArray(list)) {
          list = [];
        }
        
        const serializedList = list.map((ilce) => {
          const raw = ilce._doc || ilce;
          const id = raw._id ?? raw.id;
          const adi = raw.adi ?? raw.ad ?? raw.name;
          return {
            id,
            name: adi,
            ad: adi,
            cityId: raw.cityId
          };
        });
        
        setIlceler(serializedList);
      } catch (err) {
        console.error('İlçeler yüklenemedi:', err);
        setIlceler([]);
      } finally {
        setIlcelerLoading(false);
      }
    };
    
    loadDistricts();
  }, [newCustomerForm.cityId]);

  // Müşteri arama - debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (suppressCustomerSearchRef.current) {
        suppressCustomerSearchRef.current = false;
        setCustomerSearchResults([]);
        setCustomerSearchLoading(false);
        return;
      }
      const trimmedInput = customerSearchInput.trim();
      if (trimmedInput.length >= 3) {
        setCustomerSearchLoading(true);
        searchCustomers(sanitizeSearchInput(trimmedInput), { limit: 5, aktif: true })
          .then(response => {
            const list = response?.veri?.musteriler || response?.veri || [];
            setCustomerSearchResults(Array.isArray(list) ? list.slice(0, 5) : []);
          })
          .catch(err => {
            console.error('Müşteri arama hatası:', err);
            setCustomerSearchResults([]);
          })
          .finally(() => {
            setCustomerSearchLoading(false);
          });
      } else {
        setCustomerSearchResults([]);
        setCustomerSearchLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [customerSearchInput]);

  // Müşteri seçildiğinde cihazları yükle
  useEffect(() => {
    if (!selectedCustomer) {
      setDevices([]);
      return;
    }

    const customerId = selectedCustomer.id || selectedCustomer._id;
    
    // Cihazları yükle
    setDevicesLoading(true);
    fetchDevicesByCustomer(customerId, { page: 1, limit: 100 })
      .then(response => {
        const list = response?.veri?.cihazlar || response?.veri || [];
        setDevices(Array.isArray(list) ? list : []);
      })
      .catch(err => {
        console.error('Cihazlar yüklenemedi:', err);
        setDevices([]);
      })
      .finally(() => {
        setDevicesLoading(false);
      });
  }, [selectedCustomer]);

  const selectedCustomerId = selectedCustomer?.id || selectedCustomer?._id || null;

  const pickDefaultAddress = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.find((addr) => addr.varsayilan === true) || list[0];
  }, []);

  const normalizeAddressRow = useCallback((addr) => ({
    ...addr,
    city: addr.city || addr.il || null,
    district: addr.district || addr.ilce || null,
  }), []);

  const loadAddressesForCustomer = useCallback(async (customerId, cancelledRef) => {
    if (!customerId) return;
    setAddressesLoading(true);
    try {
      const response = await fetchAddressesByCustomer(customerId, true);
      if (cancelledRef?.current) return;
      const list = response?.veri?.adresler || response?.veri || [];
      const addressesList = (Array.isArray(list) ? list : []).map(normalizeAddressRow);
      setAddresses(addressesList);
      setSelectedAddress((prevSelected) => {
        if (prevSelected && addressesList.length > 0) {
          const stillExists = addressesList.find(
            (addr) => (addr.id || addr._id) === (prevSelected.id || prevSelected._id),
          );
          if (stillExists) return stillExists;
        }
        return pickDefaultAddress(addressesList);
      });
    } catch (err) {
      if (cancelledRef?.current) return;
      console.error('[loadAddressesForCustomer] Adresler yüklenemedi:', err);
      setAddresses([]);
      setSelectedAddress(null);
    } finally {
      if (!cancelledRef?.current) {
        setAddressesLoading(false);
      }
    }
  }, [normalizeAddressRow, pickDefaultAddress]);

  // Adresleri yükle — müşteri değişince (iller state'ine bağlı olmadan, iptal döngüsü yok)
  useEffect(() => {
    if (!selectedCustomerId) {
      setAddresses([]);
      setSelectedAddress(null);
      setAddressesLoading(false);
      return undefined;
    }

    const cancelledRef = { current: false };
    loadAddressesForCustomer(selectedCustomerId, cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [selectedCustomerId, loadAddressesForCustomer]);

  // Hub'dan yeni kayıt vb. sonrası adres listesi boş kaldıysa, adres sekmesinde yeniden yükle
  useEffect(() => {
    if (activeTab !== 'address-select' || !selectedCustomerId) return undefined;
    if (addresses.length > 0 || addressesLoading) return undefined;

    const cancelledRef = { current: false };
    loadAddressesForCustomer(selectedCustomerId, cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [activeTab, selectedCustomerId, addresses.length, addressesLoading, loadAddressesForCustomer]);


  // Tüm form state'lerini resetle
  const resetAllStates = () => {
    setSelectedOperation('');
    setSelectedFaults([]);
    setFaultNote('');
    setSelectedDevices([]);
    setDeviceNotSelected(false);
    setDeviceExplanation('');
    setSelectedAddress(null);
    setAddresses([]);
    setPriority('Orta');
    setNotes('');
    setOtpVarsayilanNumara(true);
    setOtpAlternatifTelefon('');
    setMaxAllowedTab('customer-search');
    setGorusmeKaydiId(null);
    setKurulumIcerikId('');
    setKurulumSeriNo('');
    setChecklistSablon(null);
    setChecklistCevaplari({});
    setComplaintCategory('');
    setComplaintTargetIcerikId('');
    setComplaintTargetCihazId('');
    setComplaintTargetTechServiceId('');
    setComplaintTargetServisKaydiId('');
    setComplaintNote('');
    setIstekSikayetTuru('');
    setComplaintCityId('');
    setComplaintTsSearchInput('');
    setComplaintTsResults([]);
    setComplaintMusteriServisKayitlari([]);
    setBakimFiyatOnizleme(null);
    setOdemeBeyaniChecked(false);
    setIzinModalOpen(false);
  };

  const getDraftGetters = useCallback(() => ({
    selectedOperation,
    deviceNotSelected,
    deviceExplanation,
    selectedDevices,
    selectedAddress,
    selectedFaults,
    faultNote,
    selectedTechServices,
    priority,
    notes,
    complaintCategory,
    complaintNote,
    istekSikayetTuru,
    complaintTargetIcerikId,
    complaintTargetCihazId,
    complaintTargetTechServiceId,
    complaintTargetServisKaydiId,
    kurulumIcerikId,
    kurulumSeriNo,
    checklistCevaplari,
    checklistDegerleri,
    activeTab,
    otpVarsayilanNumara,
    otpAlternatifTelefon,
    odemeBeyaniChecked,
    isSantralFlow,
    gorusmeKaydiId,
    onServisKaydiId,
    santralTelefon,
    kaliciTaslakId,
    selectedCustomer,
    maxAllowedTab,
  }), [
    selectedOperation,
    deviceNotSelected,
    deviceExplanation,
    selectedDevices,
    selectedAddress,
    selectedFaults,
    faultNote,
    selectedTechServices,
    priority,
    notes,
    complaintCategory,
    complaintNote,
    istekSikayetTuru,
    complaintTargetIcerikId,
    complaintTargetCihazId,
    complaintTargetTechServiceId,
    complaintTargetServisKaydiId,
    kurulumIcerikId,
    kurulumSeriNo,
    checklistCevaplari,
    checklistDegerleri,
    activeTab,
    otpVarsayilanNumara,
    otpAlternatifTelefon,
    odemeBeyaniChecked,
    isSantralFlow,
    gorusmeKaydiId,
    onServisKaydiId,
    santralTelefon,
    kaliciTaslakId,
    selectedCustomer,
    maxAllowedTab,
  ]);

  const getDraftSetters = useCallback(() => ({
    setSelectedOperation,
    setDeviceNotSelected,
    setDeviceExplanation,
    setSelectedDevices,
    setSelectedAddress,
    setSelectedFaults,
    setFaultNote,
    setSelectedTechServices,
    setPriority,
    setNotes,
    setComplaintCategory,
    setComplaintNote,
    setIstekSikayetTuru,
    setComplaintTargetIcerikId,
    setComplaintTargetCihazId,
    setComplaintTargetTechServiceId,
    setComplaintTargetServisKaydiId,
    setKurulumIcerikId,
    setKurulumSeriNo,
    setChecklistCevaplari,
    setChecklistDegerleri,
    setActiveTab,
    setMaxAllowedTab,
    setOtpVarsayilanNumara,
    setOtpAlternatifTelefon,
    setOdemeBeyaniChecked,
    setIsSantralFlow,
    setGorusmeKaydiId,
    setOnServisKaydiId,
    setSantralTelefon,
    setKaliciTaslakId,
    setSelectedCustomer,
    setAddresses,
    setChecklistSablon,
  }), []);

  useEffect(() => {
    if (!wizardDraft?.saveDraft) return undefined;
    if (!selectedOperation) {
      wizardDraft.clearDraft?.();
      return undefined;
    }
    const t = setTimeout(() => {
      wizardDraft.saveDraft(buildGeciciTaslakSnapshot(getDraftGetters()));
    }, 500);
    return () => clearTimeout(t);
  }, [wizardDraft, getDraftGetters, selectedOperation]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taslakId = params.get('taslakId');
    const shouldRestoreGecici = location.state?.restoreGeciciTaslak
      || wizardDraft?.consumeRestorePending?.();
    if (restoreAppliedRef.current && !taslakId) return undefined;

    const runRestore = async () => {
      if (taslakId && kaliciTaslakInitRef.current !== taslakId) {
        kaliciTaslakInitRef.current = taslakId;
        try {
          if (wizardDraft?.hasDraft?.() && !window.confirm('Geçici taslak silinsin ve kalıcı taslak açılsın mı?')) {
            return;
          }
          wizardDraft?.clearDraft?.();
          const res = await fetchServiceRecordDetail(taslakId);
          const record = res?.veri?.servisKaydi || res?.veri;
          if (record) {
            await applyKaliciTaslakRecord(record, getDraftSetters());
            restoreAppliedRef.current = true;
            navigate(location.pathname, { replace: true, state: null });
          }
        } catch (err) {
          window.showNotification?.('error', getErrorMessage(err, 'Kalıcı taslak yüklenemedi'));
        }
        return;
      }

      if (shouldRestoreGecici && wizardDraft?.draft && !restoreAppliedRef.current) {
        try {
          await applyGeciciTaslakSnapshot(wizardDraft.draft, getDraftSetters());
          restoreAppliedRef.current = true;
          if (location.state?.restoreGeciciTaslak) {
            navigate(location.pathname, { replace: true, state: null });
          }
        } catch (err) {
          console.error('Geçici taslak restore hatası:', err);
        }
      }
    };

    runRestore();
    return undefined;
  }, [
    location.search,
    location.state,
    location.pathname,
    wizardDraft,
    getDraftSetters,
    navigate,
  ]);

  const handleKaliciKaydet = async () => {
    const customerId = selectedCustomer?.id || selectedCustomer?._id;
    if (!customerId) {
      window.showNotification?.('error', 'Kalıcı taslak için önce müşteri seçin');
      return;
    }
    if (!selectedOperation) {
      window.showNotification?.('error', 'Taslak kaydetmek için önce işlem tipi seçin');
      return;
    }
    setKaliciKaydetLoading(true);
    try {
      const payload = buildKaliciTaslakPayload(getDraftGetters());
      let res;
      if (kaliciTaslakId) {
        res = await patchTaslakServisKaydi(kaliciTaslakId, payload);
      } else {
        res = await createTaslakServisKaydi(payload);
      }
      const sk = res?.veri?.servisKaydi;
      const newId = sk?.id || sk?._id;
      if (newId) setKaliciTaslakId(String(newId));
      window.showNotification?.('success', kaliciTaslakId ? 'Kalıcı taslak güncellendi' : 'Kalıcı taslak kaydedildi');
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Taslak kaydedilemedi'));
    } finally {
      setKaliciKaydetLoading(false);
    }
  };

  const loadHubData = useCallback(async (musteriId) => {
    if (!musteriId) return;
    setHubYukleniyor(true);
    setHubGorusmeler([]);
    setHubSms([]);
    setHubGorusmelerLoaded(false);
    setHubSmsLoaded(false);
    try {
      const res = await fetchServisKaydiHub(musteriId);
      const veri = res?.veri || {};
      setHubAcikKayitlar(veri.acikKayitlar || []);
      setHubGecmisKayitlar(veri.gecmisKayitlar || []);
      setHubMusteriDetay(veri.musteri || null);
      setHubAdresler(veri.adresler || []);
      setHubCihazlar(veri.cihazlar || []);
      if (veri.musteri?.iletisimIzni) {
        setSelectedCustomer((prev) => {
          const pid = prev?.id || prev?._id;
          if (!pid || String(pid) !== String(musteriId)) return prev;
          const next = veri.musteri.iletisimIzni;
          const cur = prev.iletisimIzni;
          if (
            cur?.verildi === next.verildi
            && cur?.dogrulamaTarihi === next.dogrulamaTarihi
            && cur?.dogrulamaTelefon === next.dogrulamaTelefon
          ) {
            return prev;
          }
          return { ...prev, iletisimIzni: next };
        });
      }
    } catch {
      setHubAcikKayitlar([]);
      setHubGecmisKayitlar([]);
      setHubMusteriDetay(null);
      setHubAdresler([]);
      setHubCihazlar([]);
    } finally {
      setHubYukleniyor(false);
    }
  }, []);

  const refreshSelectedCustomerIzin = useCallback(async (musteriId) => {
    if (!musteriId) return;
    try {
      const res = await fetchCustomerDetail(musteriId);
      const m = res?.veri?.musteri || res?.veri;
      if (!m?.iletisimIzni) return;
      setSelectedCustomer((prev) => {
        const pid = prev?.id || prev?._id;
        if (!pid || String(pid) !== String(musteriId)) return prev;
        return { ...prev, iletisimIzni: m.iletisimIzni };
      });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'operation-select') return;
    const mid = selectedCustomer?.id || selectedCustomer?._id;
    if (mid) refreshSelectedCustomerIzin(mid);
  }, [activeTab, selectedCustomer?.id, selectedCustomer?._id, refreshSelectedCustomerIzin]);

  const loadHubGorusmeler = useCallback(async () => {
    const musteriId = selectedCustomer?.id || selectedCustomer?._id;
    if (!musteriId || hubGorusmelerLoaded) return;
    setHubGorusmelerLoading(true);
    try {
      const res = await fetchGorusmeKayitlari({ musteriId, limit: 15 });
      setHubGorusmeler(res?.veri?.gorusmeKayitlari || []);
      setHubGorusmelerLoaded(true);
    } catch {
      setHubGorusmeler([]);
    } finally {
      setHubGorusmelerLoading(false);
    }
  }, [selectedCustomer, hubGorusmelerLoaded]);

  const loadHubSms = useCallback(async () => {
    const musteriId = selectedCustomer?.id || selectedCustomer?._id;
    if (!musteriId || hubSmsLoaded) return;
    setHubSmsLoading(true);
    try {
      const res = await getCustomerSms(musteriId, { sayfa: 1, limit: 10 });
      setHubSms(res?.veri?.smsler || []);
      setHubSmsLoaded(true);
    } catch {
      setHubSms([]);
    } finally {
      setHubSmsLoading(false);
    }
  }, [selectedCustomer, hubSmsLoaded]);

  useEffect(() => {
    if (
      activeTab !== 'device-select'
      || selectedOperation !== 'Bakım'
      || !devices.length
    ) {
      if (activeTab !== 'device-select') {
        setBakimFiyatOnizleme(null);
      }
      return undefined;
    }
    let cancelled = false;
    const cihazIds = devices
      .map((d) => d.id || d._id)
      .filter(Boolean)
      .join(',');
    if (!cihazIds) {
      setBakimFiyatOnizleme(null);
      return undefined;
    }
    setBakimFiyatOnizleme(null);
    setBakimFiyatYukleniyor(true);
    fetchBakimFiyatOnizleme({ cihazIds })
      .then((res) => {
        if (!cancelled) setBakimFiyatOnizleme(res?.veri || null);
      })
      .catch(() => {
        if (!cancelled) setBakimFiyatOnizleme({ hata: true });
      })
      .finally(() => {
        if (!cancelled) setBakimFiyatYukleniyor(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, selectedOperation, devices]);

  const bakimFiyatSatirByCihazId = useMemo(() => {
    const map = new Map();
    (bakimFiyatOnizleme?.satirlar || []).forEach((satir) => {
      if (satir?.cihazId) map.set(String(satir.cihazId), satir);
    });
    return map;
  }, [bakimFiyatOnizleme]);

  const bakimFiyatSatirBySeriIcerik = useMemo(() => {
    const map = new Map();
    (bakimFiyatOnizleme?.satirlar || []).forEach((satir) => {
      const seri = satir?.seriNumarasi ? String(satir.seriNumarasi).trim() : '';
      const icerikId = satir?.icerikId ? String(satir.icerikId) : '';
      if (!seri) return;
      const key = icerikId ? `${seri}::${icerikId}` : seri;
      if (!map.has(key)) map.set(key, satir);
    });
    return map;
  }, [bakimFiyatOnizleme]);

  useEffect(() => {
    if (selectedOperation === 'Arıza' && selectedDevices.length > 1) {
      setSelectedDevices((prev) => [prev[0]]);
    }
  }, [selectedOperation, selectedDevices.length]);

  const getNextTabAfterOperation = () => {
    if (selectedOperation === 'Kurulum') return 'product-select';
    if (selectedOperation === 'İstek ve Şikayet') return 'complaint-category-select';
    return 'device-select';
  };

  const getPreviousTabFromAddress = () => {
    if (selectedOperation === 'Arıza') return 'fault-select';
    if (selectedOperation === 'Kurulum') return 'checklist-questions';
    if (selectedOperation === 'Bakım') {
      const urunId = resolveChecklistUrunIcerikId();
      return urunId ? 'checklist-after-device' : 'device-select';
    }
    return 'device-select';
  };

  const goAfterDeviceSelect = () => {
    const urunId = resolveChecklistUrunIcerikId();
    if ((selectedOperation === 'Arıza' || selectedOperation === 'Bakım') && urunId) {
      setActiveTab('checklist-after-device');
      setMaxAllowedTab('checklist-after-device');
      return;
    }
    if (selectedOperation === 'Arıza') {
      setActiveTab('fault-select');
      setMaxAllowedTab('fault-select');
    } else {
      setActiveTab('address-select');
      setMaxAllowedTab('address-select');
    }
  };

  const goAfterChecklistStep = () => {
    if (selectedOperation === 'Kurulum') {
      setActiveTab('address-select');
      setMaxAllowedTab('address-select');
      return;
    }
    if (selectedOperation === 'Arıza') {
      setActiveTab('fault-select');
      setMaxAllowedTab('fault-select');
      return;
    }
    setActiveTab('address-select');
    setMaxAllowedTab('address-select');
  };

  useEffect(() => {
    if (activeTab === 'device-select' && selectedOperation === 'Kurulum') {
      setActiveTab(kurulumIcerikId ? 'checklist-questions' : 'product-select');
    }
  }, [activeTab, selectedOperation, kurulumIcerikId]);

  const handleOperationDevam = async () => {
    if (!selectedOperation) return;
    if (!musteriIletisimIzniVerildi(selectedCustomer)) {
      setIzinModalOpen(true);
      return;
    }
    const musteriOdemeSerhiAktif = selectedCustomer?.odemeEksikSerhi?.aktif === true;
    if (
      selectedOperation !== 'İstek ve Şikayet'
      && musteriOdemeSerhiAktif
      && !odemeBeyaniChecked
    ) {
      if (window.showNotification) {
        window.showNotification('error', 'Önceki randevuda alınamayan ödeme için müşteri ödeme beyanı zorunludur');
      }
      return;
    }
    const nextTab = getNextTabAfterOperation();
    setActiveTab(nextTab);
    setMaxAllowedTab(nextTab);
  };

  const handleYeniBasla = useCallback(() => {
    const hasContent = selectedCustomer || selectedOperation || wizardDraft?.hasDraft?.();
    if (hasContent && !window.confirm('Mevcut form ve geçici taslak silinsin, yeni kayıt başlatılsın mı?')) {
      return;
    }
    clearServiceRecordCreateCaches();
    clearSantralWizardSnapshot();
    clearManualWizardSnapshot();
    clearOnServisWizardSnapshot();
    wizardDraft?.clearDraft?.();
    clearSantralSession?.();
    onServisInitRef.current = null;
    lastSantralGorusmeRef.current = null;
    restoreAppliedRef.current = false;
    kaliciTaslakInitRef.current = null;
    setKaliciTaslakId(null);
    setSelectedCustomer(null);
    setCustomerSearchInput('');
    setCustomerSearchResults([]);
    setSelectedTechServices([]);
    setTechServiceSearchInput('');
    setTechServiceSearchResults([]);
    setIsSantralFlow(false);
    setOnServisKaydiId(null);
    setSantralTelefon(null);
    setGorusmeKaydiId(null);
    setHubAcikKayitlar([]);
    setHubGecmisKayitlar([]);
    setHubMusteriDetay(null);
    setHubAdresler([]);
    setHubCihazlar([]);
    setSelectedOperation('');
    setSelectedFaults([]);
    setFaultNote('');
    setSelectedDevices([]);
    setDeviceNotSelected(false);
    setDeviceExplanation('');
    setSelectedAddress(null);
    setAddresses([]);
    setPriority('Orta');
    setNotes('');
    setOtpVarsayilanNumara(true);
    setOtpAlternatifTelefon('');
    setKurulumIcerikId('');
    setKurulumSeriNo('');
    setChecklistSablon(null);
    setChecklistCevaplari({});
    setComplaintCategory('');
    setComplaintTargetIcerikId('');
    setComplaintTargetCihazId('');
    setComplaintTargetTechServiceId('');
    setComplaintTargetServisKaydiId('');
    setComplaintNote('');
    setIstekSikayetTuru('');
    setComplaintCityId('');
    setComplaintTsSearchInput('');
    setComplaintTsResults([]);
    setComplaintMusteriServisKayitlari([]);
    setBakimFiyatOnizleme(null);
    setOdemeBeyaniChecked(false);
    setIzinModalOpen(false);
    setActiveTab('customer-search');
    setMaxAllowedTab('customer-search');
    if (window.showNotification) {
      window.showNotification('info', 'Yeni servis kaydı başlatıldı');
    }
  }, [clearSantralSession, selectedCustomer, selectedOperation, wizardDraft]);

  const handleHubYeniKayit = async () => {
    const customer = selectedCustomer;
    const gkId = gorusmeKaydiId;
    resetAllStates();
    setSelectedCustomer(customer);
    if (gkId) setGorusmeKaydiId(gkId);
    setIsSantralFlow(true);
    setActiveTab('operation-select');
    setMaxAllowedTab('operation-select');
  };

  const handleKayitGoruntule = useCallback((kayit) => {
    const kayitId = kayit?.id || kayit?._id;
    if (!kayitId) return;
    setOpenMusteriServisKaydiId(kayitId);
  }, []);

  useEffect(() => {
    if (activeTab !== 'gorusme-hub' || !selectedCustomerId) return;
    loadHubData(selectedCustomerId);
  }, [activeTab, selectedCustomerId, loadHubData]);

  useEffect(() => {
    if (!isSantralFlow || !gorusmeKaydiId) return;
    updateSantralSession?.({
      isSantralFlow: true,
      gorusmeKaydiId,
      musteriId: selectedCustomer?.id || selectedCustomer?._id || null,
      santralTelefon,
      activeTab,
      maxAllowedTab,
      selectedCustomer,
    });
  }, [
    isSantralFlow,
    gorusmeKaydiId,
    selectedCustomer,
    santralTelefon,
    activeTab,
    maxAllowedTab,
    updateSantralSession,
  ]);

  // Müşteri seç
  const handleSelectCustomer = (customer) => {
    const existingGorusmeId = gorusmeKaydiId;
    setSelectedCustomer(customer);
    setCustomerSearchInput('');
    setCustomerSearchResults([]);
    resetAllStates();
    if (existingGorusmeId) {
      setGorusmeKaydiId(existingGorusmeId);
    }
    setMaxAllowedTab('customer-search');
    const customerId = customer?.id || customer?._id;
    if (customerId) {
      refreshSelectedCustomerIzin(customerId);
      if (existingGorusmeId) {
        updateGorusmeKaydi(existingGorusmeId, { musteriId: customerId })
          .catch((err) => console.error('Görüşme kaydı güncellenemedi:', err));
      } else if (!isSantralFlow) {
        createGorusmeKaydi({ musteriId: customerId, kaynak: 'manuel' })
          .then((res) => {
            if (res?.basarili && res?.veri?.gorusmeKaydi?.id) {
              setGorusmeKaydiId(res.veri.gorusmeKaydi.id);
            }
          })
          .catch((err) => console.error('Görüşme kaydı oluşturulamadı:', err));
      }
    }
  };

  // Müşteri seçimini iptal et
  const handleDeselectCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearchInput('');
    setCustomerSearchResults([]);
    resetAllStates();
  };

  // Arama sonucu yokken "Yeni Müşteri Oluştur"a tıklandığında
  const handleGoToCreateNewCustomer = () => {
    const trimmed = customerSearchInput.trim();
    setNewCustomerForm(prev => ({
      ...prev,
      telefon: trimmed,
    }));
    setPhonePrefilledFromSearch(trimmed.length > 0);
    setActiveTab('customer-create');
    setMaxAllowedTab('customer-create');
    if (!gorusmeKaydiId) {
      createGorusmeKaydi({ kaynak: 'manuel' })
        .then((res) => {
          if (res?.basarili && res?.veri?.gorusmeKaydi?.id) {
            setGorusmeKaydiId(res.veri.gorusmeKaydi.id);
          }
        })
        .catch((err) => console.error('Görüşme kaydı oluşturulamadı:', err));
    }
  };


  // Yeni müşteri oluştur
  const handleCreateNewCustomer = async (e) => {
    e.preventDefault();
    setNewCustomerError('');
    
    // Validasyon
    if (!newCustomerForm.adi.trim()) {
      setNewCustomerError('Ad zorunludur.');
      return;
    }
    if (!newCustomerForm.soyadi.trim()) {
      setNewCustomerError('Soyad zorunludur.');
      return;
    }
    if (!newCustomerForm.telefon.trim()) {
      setNewCustomerError('Telefon zorunludur.');
      return;
    }
    if (!newCustomerForm.adres.trim()) {
      setNewCustomerError('Adres zorunludur.');
      return;
    }
    if (!newCustomerForm.cityId || newCustomerForm.cityId.trim() === '') {
      setNewCustomerError('İl seçimi zorunludur.');
      return;
    }
    if (!newCustomerForm.districtId || newCustomerForm.districtId.trim() === '') {
      setNewCustomerError('İlçe seçimi zorunludur.');
      return;
    }

    setNewCustomerSubmitting(true);
    try {
      // Önce müşteriyi oluştur (sadece temel bilgiler)
      const customerPayload = {
        adi: newCustomerForm.adi,
        soyadi: newCustomerForm.soyadi,
        telefon: newCustomerForm.telefon,
        mail: newCustomerForm.mail,
      };
      
      const response = await createCustomer(customerPayload);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Müşteri oluşturulamadı');
      }

      const created = response?.veri?.musteri || response?.veri || {};
      const newId = created?.id || created?._id;
      
      if (newId) {
        const createdCustomer = {
          id: newId,
          adi: newCustomerForm.adi,
          soyadi: newCustomerForm.soyadi,
          telefon: newCustomerForm.telefon,
          mail: newCustomerForm.mail,
        };

        const gkId = gorusmeKaydiId;
        if (gkId) {
          try {
            await updateGorusmeKaydi(gkId, { musteriId: newId });
          } catch (e) {
            console.warn('Görüşme kaydı güncellenemedi:', e);
          }
        }

        // Müşteri oluşturulduktan sonra adres oluştur
        if (newCustomerForm.adres && newCustomerForm.cityId && newCustomerForm.districtId) {
          try {
            const addressResponse = await createAddress(newId, {
              adres: newCustomerForm.adres.trim(),
              cityId: newCustomerForm.cityId,
              districtId: newCustomerForm.districtId,
              varsayilan: true,
              aktif: true,
            });

            if (addressResponse?.basarili === false) {
              console.warn('Adres oluşturulamadı:', addressResponse?.mesaj || addressResponse?.message);
            } else {
              const createdAddress = addressResponse?.veri?.adres || addressResponse?.veri || {};
              const addressesResponse = await fetchAddressesByCustomer(newId, true);
              const addressesList = addressesResponse?.veri?.adresler || addressesResponse?.veri || [];

              setSelectedCustomer(createdCustomer);
              setCustomerSearchInput('');
              setCustomerSearchResults([]);
              resetAllStates();
              if (gkId) setGorusmeKaydiId(gkId);
              setAddresses(Array.isArray(addressesList) ? addressesList : []);
              if (createdAddress && createdAddress.id) {
                setSelectedAddress(createdAddress);
              }
              setMaxAllowedTab('customer-search');
            }
          } catch (addressError) {
            console.error('Adres oluşturma hatası:', addressError);
            setSelectedCustomer(createdCustomer);
            setCustomerSearchInput('');
            setCustomerSearchResults([]);
            resetAllStates();
            if (gkId) setGorusmeKaydiId(gkId);
            setMaxAllowedTab('customer-search');
          }
        } else {
          setSelectedCustomer(createdCustomer);
          setCustomerSearchInput('');
          setCustomerSearchResults([]);
          resetAllStates();
          if (gkId) setGorusmeKaydiId(gkId);
          setMaxAllowedTab('customer-search');
        }

        // Formu sıfırla ve ilk adıma (Müşteri Arama) dön
        setNewCustomerForm({
          adi: '',
          soyadi: '',
          telefon: '',
          mail: '',
          adres: '',
          cityId: '',
          districtId: '',
        });
        setPhonePrefilledFromSearch(false);
        setActiveTab('customer-search');

        if (window.showNotification) {
          window.showNotification('success', 'Müşteri oluşturuldu. Devam butonuna tıklayarak ilerleyebilirsiniz.');
        }
      }
    } catch (error) {
      console.error('Create customer error:', error);
      setNewCustomerError(getErrorMessage(error, 'Müşteri oluşturulamadı'));
    } finally {
      setNewCustomerSubmitting(false);
    }
  };

  // Cihaz checkbox / radio değişikliği (Bakım: çoklu, Arıza: tek)
  const handleDeviceToggle = (deviceId) => {
    if (deviceNotSelected) return;

    if (selectedOperation === 'Arıza') {
      setSelectedDevices((prev) => (prev.includes(deviceId) ? [] : [deviceId]));
      return;
    }

    setSelectedDevices((prev) => {
      if (prev.includes(deviceId)) {
        return prev.filter((id) => id !== deviceId);
      }
      return [...prev, deviceId];
    });
  };

  const getDeviceBakimFiyatLabel = useCallback((device) => {
    const urun = device?.urun || device?.icerik;
    const bf = urun?.bakimFiyati;
    const fp = urun?.fabrikaPayi;
    if (bf != null && Number.isFinite(Number(bf)) && fp != null && Number.isFinite(Number(fp))) {
      return `${Number(bf).toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ₺`;
    }
    if (bakimFiyatYukleniyor) return 'Yükleniyor…';
    if (bakimFiyatOnizleme?.hata) return 'Alınamadı';
    const deviceId = device?.id || device?._id;
    const seri = device?.seriNumarasi ? String(device.seriNumarasi).trim() : '';
    const icerikId = device?.icerikId || urun?.id;
    const satir = (deviceId && bakimFiyatSatirByCihazId.get(String(deviceId)))
      || (seri && icerikId && bakimFiyatSatirBySeriIcerik.get(`${seri}::${String(icerikId)}`))
      || (seri && bakimFiyatSatirBySeriIcerik.get(seri));
    if (satir?.bakimFiyati != null && Number.isFinite(Number(satir.bakimFiyati))) {
      return `${Number(satir.bakimFiyati).toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ₺`;
    }
    const urunAd = urun?.urunAdi || urun?.icerikAdi || '';
    if (
      urunAd
      && (
        (bf == null || fp == null)
        || (
          Array.isArray(bakimFiyatOnizleme?.eksikUrunler)
          && bakimFiyatOnizleme.eksikUrunler.some((e) => String(e).includes(urunAd) || urunAd.includes(String(e)))
        )
      )
    ) {
      return 'Eksik tanım';
    }
    return '—';
  }, [bakimFiyatOnizleme, bakimFiyatYukleniyor, bakimFiyatSatirByCihazId, bakimFiyatSatirBySeriIcerik]);

  // Cihaz seçilmeyecek checkbox değişikliği
  const handleDeviceNotSelectedToggle = (checked) => {
    setDeviceNotSelected(checked);
    if (checked) {
      setSelectedDevices([]);
    }
  };

  // Adres seç
  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
  };

  // Adres ve cihaz seçildiğinde yetkili teknik servisleri yükle (service-areas)
  useEffect(() => {
    if (!selectedAddress || !selectedAddress.cityId) {
      setTechServices([]);
      setSelectedTechServices([]);
      return;
    }

    const cityId = selectedAddress.cityId?.id || selectedAddress.cityId;
    const districtId = selectedAddress.districtId?.id || selectedAddress.districtId;

    if (!districtId) {
      // İlçe yoksa yetkili servisler API districtId zorunlu; fallback olarak eski API
      setTechServicesLoading(true);
      fetchTechServices({
        cityId: parseInt(cityId, 10),
        aktif: true,
        page: 1,
        limit: 100,
      })
        .then((response) => {
          const list = response?.veri?.teknikServisler || response?.veri || [];
          const services = Array.isArray(list) ? list : [];
          setTechServices(services);
          if (services.length > 0) setSelectedTechServices([services[0]]);
        })
        .catch(() => setTechServices([]))
        .finally(() => setTechServicesLoading(false));
      return;
    }

    const loadAuthorizedTechServices = async () => {
      setTechServicesLoading(true);
      try {
        let allServices = [];
        const dId = parseInt(districtId, 10);

        const kategoriIds = new Set();
        const urunIcerikIds = new Set();
        if (selectedOperation === 'Kurulum' && kurulumIcerikId && kurulumUrunler && kurulumUrunler.length > 0) {
          const product = kurulumUrunler.find((p) => (p.id || p._id) === kurulumIcerikId);
          urunIcerikIds.add(String(kurulumIcerikId));
          const kid = product?.kategoriId || product?.kategori?._id || product?.kategori?.id;
          if (kid) kategoriIds.add(String(kid));
        } else if (selectedDevices && selectedDevices.length > 0 && !deviceNotSelected) {
          for (const device of selectedDevices) {
            const prod = device.urun || device.icerik;
            const uid = prod?.id || prod?._id;
            if (uid) urunIcerikIds.add(String(uid));
            const kid = prod?.kategoriId || prod?.kategori?._id || prod?.kategori?.id;
            if (kid) kategoriIds.add(String(kid));
          }
        }

        for (const uid of urunIcerikIds) {
          const res = await fetchAuthorizedTechServices({ districtId: dId, urunIcerikId: uid });
          const list = res?.veri?.teknikServisler || [];
          for (const s of list) {
            const sid = s.id || s._id;
            if (sid && !allServices.find((x) => (x.id || x._id) === sid)) {
              allServices.push(s);
            }
          }
        }

        if (kategoriIds.size > 0 && allServices.length === 0) {
          const rootIds = new Set();
          for (const kid of kategoriIds) {
            try {
              const rootRes = await fetchCategoryRoot(kid);
              const root = rootRes?.veri?.anaKategori;
              if (root?.id) rootIds.add(root.id);
              else rootIds.add(kid);
            } catch {
              rootIds.add(kid);
            }
          }
          for (const rootId of rootIds) {
            const res = await fetchAuthorizedTechServices({ districtId: dId, kategoriId: rootId });
            const list = res?.veri?.teknikServisler || [];
            for (const s of list) {
              const sid = s.id || s._id;
              if (sid && !allServices.find((x) => (x.id || x._id) === sid)) {
                allServices.push(s);
              }
            }
          }
        }

        if (allServices.length === 0) {
          const res = await fetchAuthorizedTechServices({ districtId: dId });
          allServices = res?.veri?.teknikServisler || res?.veri || [];
          allServices = Array.isArray(allServices) ? allServices : [];
        }

        setTechServices(allServices);
        if (allServices.length > 0) setSelectedTechServices([allServices[0]]);
        else setSelectedTechServices([]);
      } catch (err) {
        console.error('Yetkili teknik servisler yüklenemedi:', err);
        setTechServices([]);
        setSelectedTechServices([]);
      } finally {
        setTechServicesLoading(false);
      }
    };

    loadAuthorizedTechServices();
  }, [selectedAddress, selectedDevices, deviceNotSelected, selectedOperation, kurulumIcerikId, kurulumUrunler]);

  // Teknik servis arama
  useEffect(() => {
    if (!techServiceSearchInput || techServiceSearchInput.trim().length < 2) {
      setTechServiceSearchResults([]);
      setTechServiceSearchLoading(false);
      return;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      if (isCancelled) return;
      
      setTechServiceSearchLoading(true);
      setTechServiceSearchResults([]);
      
      try {
        const term = sanitizeSearchInput(techServiceSearchInput, { trimEdges: false }).trim();
        console.log('[ServiceRecordCreatePage] Teknik servis arama başlatılıyor:', term);

        const response = await searchTechServices(term, {
          aktif: true,
          sayfa: 1,
          limit: 80,
        });
        
        if (isCancelled) return;
        
        console.log('[ServiceRecordCreatePage] Teknik servis arama response:', response);
        
        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Teknik servis arama başarısız');
        }
        
        const list = response?.veri?.teknikServisler || response?.veri || [];
        console.log('[ServiceRecordCreatePage] Teknik servis listesi:', list);
        setTechServiceSearchResults(Array.isArray(list) ? list : []);
      } catch (err) {
        if (isCancelled) return;
        console.error('[ServiceRecordCreatePage] Teknik servis arama hatası:', err);
        console.error('[ServiceRecordCreatePage] Hata detayları:', {
          message: err?.message,
          stack: err?.stack,
          data: err?.data,
          status: err?.status
        });
        setTechServiceSearchResults([]);
        
        // Hata mesajını göster
        const errorMessage = err?.message || err?.data?.mesaj || err?.data?.message || 'Teknik servis arama sırasında bir hata oluştu. Lütfen tekrar deneyin.';
        console.error('[ServiceRecordCreatePage] Hata mesajı:', errorMessage);
        
        // React hatası değilse notification göster
        if (err?.name !== 'Error' || err?.message?.includes('render')) {
          // React render hatası değilse notification göster
          if (window.showNotification) {
            window.showNotification('error', errorMessage);
          }
        }
      } finally {
        if (!isCancelled) {
          setTechServiceSearchLoading(false);
        }
      }
    }, 500);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [techServiceSearchInput]);

  // İstek ve Şikayet — Teknik Servis aramasını il filtresi + arama metni ile çalıştır
  useEffect(() => {
    if (selectedOperation !== 'İstek ve Şikayet' || complaintCategory !== 'Teknik Servis') {
      return undefined;
    }
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setComplaintTsLoading(true);
        const term = (complaintTsSearchInput || '').trim();
        if (term.length >= 2) {
          const res = await searchTechServices(term, {
            cityId: complaintCityId || undefined,
            aktif: true,
            sayfa: 1,
            limit: 30,
          });
          if (cancelled) return;
          const list = res?.veri?.teknikServisler || res?.veri || [];
          setComplaintTsResults(Array.isArray(list) ? list : []);
        } else {
          const res = await fetchTechServices({
            cityId: complaintCityId || undefined,
            aktif: true,
            page: 1,
            limit: 30,
            sortBy: 'ad',
            sortOrder: 'asc',
          });
          if (cancelled) return;
          const list = res?.veri?.teknikServisler || res?.veri || [];
          setComplaintTsResults(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[Complaint] Teknik servis araması başarısız:', err);
          setComplaintTsResults([]);
        }
      } finally {
        if (!cancelled) setComplaintTsLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedOperation, complaintCategory, complaintTsSearchInput, complaintCityId]);

  // İstek ve Şikayet — Müşterinin geçmiş servis kayıtlarını yükle
  useEffect(() => {
    if (selectedOperation !== 'İstek ve Şikayet' || complaintCategory !== 'Teknik Servis' || !selectedCustomer) {
      return;
    }
    let cancelled = false;
    const customerId = selectedCustomer.id || selectedCustomer._id;
    if (!customerId) return;
    setComplaintMusteriServisKayitlariLoading(true);
    fetchServiceRecords({ musteriId: customerId, aktif: true, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        const list = res?.veri?.servisKayitlari || res?.veri || [];
        setComplaintMusteriServisKayitlari(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[Complaint] Müşterinin servis kayıtları yüklenemedi:', err);
          setComplaintMusteriServisKayitlari([]);
        }
      })
      .finally(() => {
        if (!cancelled) setComplaintMusteriServisKayitlariLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOperation, complaintCategory, selectedCustomer]);

  // Yeni adres ekle
  const handleAddNewAddress = async () => {
    if (!selectedCustomer || !newAddressText.trim()) return;
    
    if (!newAddressCityId || !newAddressDistrictId) {
      if (window.showNotification) {
        window.showNotification('error', 'İl ve ilçe seçimi gereklidir');
      }
      return;
    }
    
    setAddressSubmitting(true);
    try {
      const customerId = selectedCustomer.id || selectedCustomer._id;
      const response = await createAddress(customerId, {
        adres: newAddressText.trim(),
        cityId: newAddressCityId,
        districtId: newAddressDistrictId,
        varsayilan: true,
        aktif: true,
      });
      
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Adres eklenemedi');
      }
      
      const newAddress = response?.veri?.adres || response?.veri || {};
      
      // Adres listesini güncelle
      setAddresses(prev => [...prev, newAddress]);
      setSelectedAddress(newAddress);
      setNewAddressText('');
      setNewAddressCityId('');
      setNewAddressDistrictId('');
      setIsAddressModalOpen(false);
      
      if (window.showNotification) {
        window.showNotification('success', 'Adres eklendi ve varsayılan yapıldı');
      }
    } catch (error) {
      console.error('Adres ekleme hatası:', error);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(error, 'Adres eklenemedi'));
      }
    } finally {
      setAddressSubmitting(false);
    }
  };


  // Servis kaydını oluştur
  const handleCreateServiceRecord = async () => {
    if (!selectedCustomer) {
      if (window.showNotification) {
        window.showNotification('error', 'Müşteri seçilmedi');
      }
      return;
    }
    
    if (!selectedOperation) {
      if (window.showNotification) {
        window.showNotification('error', 'İşlem tipi seçilmedi');
      }
      return;
    }
    
    // İstek ve Şikayet'te kategori + not zorunlu, adres/cihaz/teknik servis opsiyonel
    if (isIstekSikayet) {
      if (!complaintCategory) {
        if (window.showNotification) window.showNotification('error', 'İstek/Şikayet kategorisi seçilmedi');
        return;
      }
      if (!complaintNote || !complaintNote.trim()) {
        if (window.showNotification) window.showNotification('error', 'İstek/Şikayet notu zorunludur');
        return;
      }
      if (!istekSikayetTuru || !['İstek', 'Şikayet'].includes(istekSikayetTuru)) {
        if (window.showNotification) window.showNotification('error', 'Kayıt türü seçin: İstek veya Şikayet');
        return;
      }
    } else {
      // Kurulum'da cihaz seçimi yok; diğer işlemlerde cihaz veya "cihaz seçmek istemiyorum" gerekli
      if (selectedOperation !== 'Kurulum' && !deviceNotSelected && selectedDevices.length === 0) {
        if (window.showNotification) {
          window.showNotification('error', 'Cihaz seçimi yapılmadı veya cihaz seçmek istemiyorum seçeneği işaretlenmedi');
        }
        return;
      }

      if (selectedOperation === 'Arıza' && !deviceNotSelected && selectedDevices.length > 1) {
        if (window.showNotification) {
          window.showNotification('error', 'Arıza kaydında yalnızca bir cihaz seçilebilir');
        }
        return;
      }

      if (!selectedAddress) {
        if (window.showNotification) {
          window.showNotification('error', 'Adres seçilmedi');
        }
        return;
      }

      if (!priority) {
        if (window.showNotification) {
          window.showNotification('error', 'Önem derecesi seçilmedi');
        }
        return;
      }

      if (!otpVarsayilanNumara) {
        const digits = String(otpAlternatifTelefon || '').replace(/\D/g, '');
        if (digits.startsWith('0')) {
          if (window.showNotification) {
            window.showNotification('error', 'OTP SMS telefonu başında 0 olmadan 10 hane girilmelidir');
          }
          return;
        }
        if (digits.length !== 10 || !/^5[0-9]{9}$/.test(normalizeTurkishPhone(digits))) {
          if (window.showNotification) {
            window.showNotification('error', 'OTP SMS için geçerli 10 haneli GSM numarası girin');
          }
          return;
        }
      }
    }

    const musteriOdemeSerhiAktif = selectedCustomer?.odemeEksikSerhi?.aktif === true;
    if (!isIstekSikayet && musteriOdemeSerhiAktif && !odemeBeyaniChecked) {
      if (window.showNotification) {
        window.showNotification('error', 'Önceki randevuda alınamayan ödeme için müşteri ödeme beyanı zorunludur');
      }
      return;
    }

    setFinalSubmitting(true);
    try {
      const customerId = selectedCustomer.id || selectedCustomer._id;
      const addressId = selectedAddress ? (selectedAddress.id || selectedAddress._id) : null;

      let checklistPayload = buildChecklistTelefonPayload();
      const checklistEligibleOps = ['Kurulum', 'Arıza', 'Bakım'];
      const checklistUrunId = resolveChecklistUrunIcerikId();
      if (checklistEligibleOps.includes(selectedOperation) && checklistUrunId && !checklistPayload) {
        let sablon = checklistSablon;
        if (!sablon) {
          try {
            const clRes = await fetchChecklistSablonByUrun(checklistUrunId, { islemTipi: selectedOperation });
            sablon = clRes?.veri?.checklistSablon || null;
            if (sablon) setChecklistSablon(sablon);
          } catch {
            /* checklist yüklenemedi */
          }
        }
        if (sablon) {
          const sorular = (sablon.sorular || []).filter((s) => s.cagriMerkezi === true && s.pasif !== true);
          if (sorular.length > 0) {
            const eksik = sorular.filter((s) => {
              const sid = String(s._id || s.id);
              const cevap = checklistCevaplari[sid];
              if (cevap !== 'evet' && cevap !== 'hayir') return true;
              if (cevap === 'hayir') return false;
              if (s.degerAlani && !(checklistDegerleri[sid] || '').trim()) return true;
              return false;
            });
            if (eksik.length > 0) {
              if (window.showNotification) {
                window.showNotification('error', 'Kontrol listesi sorularının tamamına cevap verin');
              }
              setFinalSubmitting(false);
              return;
            }
            if (sorular.some((s) => checklistCevaplari[String(s._id || s.id)] === 'hayir')) {
              if (window.showNotification) {
                window.showNotification('error', 'Kontrol listesi karşılanmadı');
              }
              setFinalSubmitting(false);
              return;
            }
            checklistPayload = buildChecklistTelefonPayload(sablon);
            if (!checklistPayload) {
              if (window.showNotification) {
                window.showNotification('error', 'Kontrol listesi cevapları gönderilemedi. Lütfen tekrar deneyin.');
              }
              setFinalSubmitting(false);
              return;
            }
          }
        }
      }

      if (
        checklistEligibleOps.includes(selectedOperation)
        && checklistUrunId
        && cagriMerkeziSorular.length > 0
        && !checklistPayload
      ) {
        if (window.showNotification) {
          window.showNotification('error', 'Kontrol listesi cevapları gönderilemedi. Lütfen tekrar deneyin.');
        }
        setFinalSubmitting(false);
        return;
      }

      // İşlem tipini kontrol et ve doğru formatta gönder
      const islemTipi = selectedOperation && String(selectedOperation).trim();
      if (!islemTipi || !['Kurulum', 'Bakım', 'Arıza', 'İstek ve Şikayet'].includes(islemTipi)) {
        if (window.showNotification) {
          window.showNotification('error', 'Geçersiz işlem tipi seçildi');
        }
        setFinalSubmitting(false);
        return;
      }
      
      // Debug: Priority değerini kontrol et
      console.log('[ServiceRecordCreatePage] Priority değeri:', {
        priority,
        type: typeof priority,
        length: priority?.length,
        charCodes: priority ? Array.from(priority).map(c => c.charCodeAt(0)) : []
      });
      
      const payload = isIstekSikayet
        ? {
            musteriId: customerId,
            islemTipi,
            onemDerecesi: priority ? String(priority).trim() : 'Orta',
            istekSikayetKategorisi: complaintCategory,
            istekSikayetTuru,
            istekSikayetNotu: complaintNote.trim(),
          }
        : {
            musteriId: customerId,
            islemTipi: islemTipi,
            cihazIds: islemTipi === 'Kurulum' ? [] : selectedDevices,
            cihazSecilmedi: islemTipi === 'Kurulum' ? true : deviceNotSelected,
            cihazAciklama: islemTipi === 'Kurulum' ? '' : (deviceNotSelected ? deviceExplanation : ''),
            adresId: addressId,
            onemDerecesi: priority ? String(priority).trim() : priority,
            notlar: notes,
          };

      if (!isIstekSikayet && odemeBeyaniChecked) {
        payload.odemeBeyani = true;
      }

      if (isIstekSikayet) {
        if (complaintCategory === 'Ürün') {
          if (complaintTargetIcerikId) payload.hedefIcerikId = complaintTargetIcerikId;
          if (complaintTargetCihazId) payload.hedefCihazId = complaintTargetCihazId;
        } else if (complaintCategory === 'Teknik Servis') {
          if (complaintTargetTechServiceId) payload.hedefTeknikServisId = complaintTargetTechServiceId;
          if (complaintTargetCihazId) payload.hedefCihazId = complaintTargetCihazId;
          if (complaintTargetServisKaydiId) payload.hedefServisKaydiId = complaintTargetServisKaydiId;
        }
      }
      
      // Teknik servis ID'leri varsa ekle (array olarak) — İstek ve Şikayet'te randevu/teknik servis yok
      if (!isIstekSikayet && selectedTechServices.length > 0) {
        const teknikServisIdArray = selectedTechServices
          .map(ts => {
            const id = ts.id || ts._id;
            if (!id) {
              console.warn('[ServiceRecordCreatePage] Teknik servis ID bulunamadı:', ts);
            }
            return id;
          })
          .filter(id => id !== null && id !== undefined);
        
        if (teknikServisIdArray.length > 0) {
          payload.teknikServisId = teknikServisIdArray;
          console.log('[ServiceRecordCreatePage] selectedTechServices:', selectedTechServices);
          console.log('[ServiceRecordCreatePage] teknikServisIdArray:', teknikServisIdArray);
        } else {
          console.error('[ServiceRecordCreatePage] Geçerli teknik servis ID bulunamadı!');
        }
      } else {
        console.log('[ServiceRecordCreatePage] selectedTechServices boş!');
      }
      
      // Arıza seçildiyse arıza bilgilerini ekle
      if (selectedOperation === 'Arıza') {
        if (selectedFaults.length > 0) {
          payload.arizaIds = selectedFaults.map(f => f.id || f._id);
        }
        if (faultNote && faultNote.trim()) {
          payload.arizaNotu = faultNote.trim();
        }
      }

      if (selectedOperation === 'Kurulum' && kurulumIcerikId) {
        payload.kurulumIcerikId = kurulumIcerikId;
        if (kurulumSeriNo && kurulumSeriNo.trim()) {
          payload.kurulumSeriNo = kurulumSeriNo.trim();
        }
      }

      if (checklistPayload && checklistEligibleOps.includes(selectedOperation)) {
        payload.checklistTelefonCevaplari = checklistPayload;
      }

      if (!isIstekSikayet && !otpVarsayilanNumara) {
        payload.otpSmsTelefon = normalizeTurkishPhone(otpAlternatifTelefon);
      }
      
      // Payload'ı console'a yaz
      console.log('[ServiceRecordCreatePage] POST /api/service-records - Payload:', JSON.stringify(payload, null, 2));
      
      const response = kaliciTaslakId
        ? await tamamlaTaslakServisKaydi(kaliciTaslakId, payload)
        : await createServiceRecord(payload);
      
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Servis kaydı oluşturulamadı');
      }
      
      const serviceRecordId = response?.veri?.servisKaydi?._id || response?.veri?.servisKaydi?.id;

      if (gorusmeKaydiId && serviceRecordId) {
        try {
          await updateGorusmeKaydi(gorusmeKaydiId, {
            durum: 'ServisKaydiOlusturuldu',
            servisKaydiId: serviceRecordId,
          });
        } catch (e) {
          console.warn('Görüşme kaydı güncellenemedi:', e);
        }
      }
      
      if (window.showNotification) {
        if (isIstekSikayet) {
          window.showNotification('success', 'İstek/Şikayet kaydı oluşturuldu.');
        } else {
          window.showNotification('success', 'Servis kaydı başarıyla oluşturuldu. Randevu oluşturma sayfasına yönlendiriliyorsunuz.');
        }
      }

      clearSantralWizardSnapshot();
      clearSantralSession?.();
      if (onServisKaydiId) {
        clearOnServisWizardSnapshot();
      }
      clearManualWizardSnapshot();
      wizardDraft?.clearDraft?.();
      setKaliciTaslakId(null);
      restoreAppliedRef.current = false;

      if (isIstekSikayet) {
        navigate('/complaint-records');
      } else if (serviceRecordId) {
        navigate(`/service-records/${serviceRecordId}/update`);
      } else {
        navigate('/service-records');
      }
    } catch (error) {
      console.error('Servis kaydı oluşturma hatası:', error);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(error, 'Servis kaydı oluşturulamadı'));
      }
    } finally {
      setFinalSubmitting(false);
    }
  };

  // Görüşmeyi sonlandır - modal aç
  const handleGorusmeyiSonlandirClick = () => {
    if (!gorusmeKaydiId) return;
    const musteriTelefon = selectedCustomer?.telefon || selectedCustomer?.phone || santralTelefon || '';
    setGorusmeSonlandirForm({
      telefon: musteriTelefon,
      sonlanmaSebebi: '',
      notlar: '',
    });
    setGorusmeSonlandirModalOpen(true);
  };

  // Görüşmeyi sonlandır - kaydet
  const handleGorusmeSonlandirSubmit = async () => {
    if (!gorusmeKaydiId) return;
    if (!gorusmeSonlandirForm.sonlanmaSebebi) {
      if (window.showNotification) window.showNotification('error', 'Lütfen sonlanma sebebini seçiniz.');
      return;
    }
    setGorusmeSonlandirLoading(true);
    try {
      const payload = {
        durum: 'GorusmeSonlandirildi',
        telefon: gorusmeSonlandirForm.telefon || null,
        sonlanmaSebebi: gorusmeSonlandirForm.sonlanmaSebebi,
        notlar: gorusmeSonlandirForm.notlar || null,
        cagriBitis: new Date().toISOString(),
      };
      if (selectedCustomer) {
        payload.musteriId = selectedCustomer.id || selectedCustomer._id;
      }
      await updateGorusmeKaydi(gorusmeKaydiId, payload);
      setGorusmeSonlandirModalOpen(false);
      clearServiceRecordCreateCaches();
      try {
        hangupCall?.();
      } catch {
        /* ignore */
      }
      if (window.showNotification) window.showNotification('success', 'Görüşme sonlandırıldı ve kayıt altına alındı.');
      clearSantralWizardSnapshot();
      clearSantralSession?.();
      navigate('/service-records');
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setGorusmeSonlandirLoading(false);
    }
  };

  // Arıza ekle (ana sayfa tree view'dan)
  const handleAddFault = (fault) => {
    const faultId = fault.id || fault._id;
    if (!selectedFaults.find(f => (f.id || f._id) === faultId)) {
      setSelectedFaults([...selectedFaults, fault]);
    }
  };

  // Arıza ekle (modal'dan)
  const handleAddFaultsFromModal = () => {
    // Modal'daki seçimleri ana listeye ekle (zaten seçili olanları ekleme)
    const newFaults = modalSelectedFaults.filter(modalFault => {
      const modalFaultId = modalFault.id || modalFault._id;
      return !selectedFaults.find(f => (f.id || f._id) === modalFaultId);
    });
    setSelectedFaults([...selectedFaults, ...newFaults]);
    setIsFaultSearchModalOpen(false);
    setModalSelectedFaults([]);
  };

  // Modal içinde arıza seç/kaldır
  const handleModalFaultToggle = (fault) => {
    const faultId = fault.id || fault._id;
    setModalSelectedFaults(prev => {
      const exists = prev.find(f => (f.id || f._id) === faultId);
      if (exists) {
        return prev.filter(f => (f.id || f._id) !== faultId);
      } else {
        return [...prev, fault];
      }
    });
  };

  const getFaultNodeId = (node) => String(node?.id || node?._id || '');

  const getFaultNodeChildren = (node) => node?.altKategoriler || node?.children || [];

  const getFaultNodeFaults = (node) => node?.arizaKayitlari || node?.faults || [];

  const countFaultsInNode = (node) => {
    let count = getFaultNodeFaults(node).length;
    getFaultNodeChildren(node).forEach((child) => {
      count += countFaultsInNode(child);
    });
    return count;
  };

  const countSelectedInNode = (node) => {
    let count = getFaultNodeFaults(node).filter((fault) =>
      modalSelectedFaults.some((f) => (f.id || f._id) === (fault.id || fault._id))
    ).length;
    getFaultNodeChildren(node).forEach((child) => {
      count += countSelectedInNode(child);
    });
    return count;
  };

  const collectAllFaultCategoryIds = (nodes) => {
    const ids = {};
    const walk = (nodeList) => {
      (nodeList || []).forEach((node) => {
        const id = getFaultNodeId(node);
        const children = getFaultNodeChildren(node);
        const faults = getFaultNodeFaults(node);
        if (children.length > 0 || faults.length > 0) {
          ids[id] = true;
        }
        walk(children);
      });
    };
    walk(nodes);
    return ids;
  };

  const toggleFaultAccordion = (nodeId) => {
    setFaultAccordionOpenIds((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const expandAllFaultAccordions = () => {
    setFaultAccordionOpenIds(collectAllFaultCategoryIds(faultCategories));
  };

  const collapseAllFaultAccordions = () => {
    setFaultAccordionOpenIds({});
  };

  // Arıza kaldır (ana listeden)
  const handleRemoveFault = (faultId) => {
    setSelectedFaults(selectedFaults.filter(f => (f.id || f._id) !== faultId));
  };

  // Tree view için kategorileri ve arıza kayıtlarını organize et
  const buildFaultTree = () => {
    // Backend'den gelen tree yapısını kullan veya manuel olarak oluştur
    if (faultCategories.length === 0) return [];
    
    // Backend tree view endpoint'inden gelen yapıyı kullan
    const processNode = (node) => {
      const processedNode = {
        ...node,
        id: node._id || node.id,
        type: 'category',
        children: [],
        faults: []
      };
      
      // Alt kategorileri işle
      if (node.altKategoriler && Array.isArray(node.altKategoriler)) {
        processedNode.children = node.altKategoriler.map(child => processNode(child));
      }
      
      // Arıza kayıtlarını ekle
      if (node.arizaKayitlari && Array.isArray(node.arizaKayitlari)) {
        processedNode.faults = node.arizaKayitlari.map(fault => ({
          ...fault,
          id: fault._id || fault.id,
          type: 'fault'
        }));
      }
      
      return processedNode;
    };
    
    return faultCategories.map(cat => processNode(cat));
  };

  // Tree view render component (ana sayfa için)
  const renderFaultTreeNode = (node, level = 0) => {
    const indent = level * 24;
    
    return (
      <div key={node.id || node._id} style={{ marginLeft: `${indent}px`, marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: level % 2 === 0 ? '#f8fafc' : '#ffffff', borderRadius: '6px' }}>
          <i className={`fas ${node.type === 'category' ? 'fa-folder' : 'fa-bug'}`} style={{ color: node.type === 'category' ? '#2563eb' : '#ef4444' }}></i>
          <span style={{ flex: 1, fontWeight: node.type === 'category' ? 500 : 400 }}>
            {node.kategoriAdi || node.arizaAdi || node.ad || '—'}
          </span>
          {node.type === 'fault' && (
            <button
              type="button"
              className="primary-btn"
              onClick={() => handleAddFault(node)}
              style={{ padding: '4px 12px', fontSize: '12px' }}
              disabled={selectedFaults.find(f => (f.id || f._id) === (node.id || node._id))}
            >
              <i className="fas fa-plus"></i>
              Ekle
            </button>
          )}
        </div>
        
        {/* Alt kategoriler */}
        {node.children && node.children.length > 0 && (
          <div style={{ marginTop: '4px' }}>
            {node.children.map(child => renderFaultTreeNode(child, level + 1))}
          </div>
        )}
        
        {/* Bu kategorideki arıza kayıtları */}
        {node.faults && node.faults.length > 0 && (
          <div style={{ marginTop: '4px' }}>
            {node.faults.map(fault => renderFaultTreeNode(fault, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Modal için tree view render (checkbox'lı, akordeon)
  const renderModalFaultTree = (node, level = 0) => {
    const nodeId = getFaultNodeId(node);
    const faultsArray = getFaultNodeFaults(node);
    const children = getFaultNodeChildren(node);
    const hasBody = faultsArray.length > 0 || children.length > 0;
    const open = faultAccordionOpenIds[nodeId] === true;
    const totalFaultCount = countFaultsInNode(node);
    const selectedInNode = countSelectedInNode(node);

    return (
      <div key={nodeId} style={{ marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => hasBody && toggleFaultAccordion(nodeId)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            background: open ? '#f1f5f9' : '#ffffff',
            cursor: hasBody ? 'pointer' : 'default',
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '14px',
            color: '#1f2937',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <span
            style={{
              width: 18,
              display: 'inline-flex',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '12px',
              flexShrink: 0,
            }}
            aria-hidden
          >
            {hasBody ? (
              <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} />
            ) : (
              <span style={{ width: 12 }} />
            )}
          </span>
          <i className="fas fa-folder" style={{ color: '#2563eb', flexShrink: 0 }} aria-hidden />
          <span style={{ flex: 1, minWidth: 0 }}>
            {node.kategoriAdi || node.ad || '—'}
          </span>
          {totalFaultCount > 0 && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#64748b',
                flexShrink: 0,
              }}
            >
              {totalFaultCount} arıza
            </span>
          )}
          {selectedInNode > 0 && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#2563eb',
                background: '#dbeafe',
                padding: '2px 8px',
                borderRadius: '999px',
                flexShrink: 0,
              }}
            >
              {selectedInNode} seçili
            </span>
          )}
        </button>

        {open && hasBody && (
          <div
            style={{
              marginTop: 8,
              paddingLeft: level > 0 ? 14 : 4,
              borderLeft: level > 0 ? '2px solid #e2e8f0' : 'none',
            }}
          >
            {faultsArray.length > 0 && (
              <div style={{ marginBottom: children.length > 0 ? 8 : 0 }}>
                {faultsArray.map((fault) => {
                  const faultId = fault.id || fault._id;
                  const isChecked = modalSelectedFaults.some((f) => (f.id || f._id) === faultId);
                  return (
                    <div
                      key={faultId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        background: isChecked ? '#eff6ff' : '#ffffff',
                        border: `1px solid ${isChecked ? '#93c5fd' : 'var(--border-color)'}`,
                        borderRadius: 6,
                        marginBottom: 4,
                        cursor: 'pointer',
                        transition: 'background 0.2s, border-color 0.2s',
                      }}
                      onClick={() => handleModalFaultToggle(fault)}
                    >
                      <input
                        type="checkbox"
                        checked={!!isChecked}
                        onChange={() => handleModalFaultToggle(fault)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: 16,
                          height: 16,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 400, fontSize: 13, lineHeight: 1.4 }}>
                          {fault.icerikAdi || fault.arizaAdi || fault.ad || '—'}
                        </div>
                        {fault.aciklama && (
                          <div style={{ fontSize: 11, color: 'var(--text-gray)', lineHeight: 1.3, marginTop: 2 }}>
                            {fault.aciklama}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {children.map((child) => renderModalFaultTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Responsive için window size
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Progress bar için step pozisyonları
  const stepsContainerRef = useRef(null);
  const [progressBarStyle, setProgressBarStyle] = useState({ left: 0, right: 0, width: '0%' });

  // Aktif tab'ın index'ini bul
  const isIstekSikayet = selectedOperation === 'İstek ve Şikayet';
  const visibleTabs = TABS.filter(tab => {
    if (tab.key === 'gorusme-hub' && !isSantralFlow) {
      return false;
    }
    if (tab.conditional === true && tab.key === 'fault-select' && selectedOperation !== 'Arıza') {
      return false;
    }
    if (tab.conditional === 'Kurulum' && selectedOperation !== 'Kurulum') {
      return false;
    }
    if (tab.conditional === 'ArızaBakım' && selectedOperation !== 'Arıza' && selectedOperation !== 'Bakım') {
      return false;
    }
    // İstek ve Şikayet seçilmediyse complaint sekmeleri gizli
    if (typeof tab.conditional === 'string' && tab.conditional.startsWith('İstek ve Şikayet') && !isIstekSikayet) {
      return false;
    }
    // Ürün/Teknik Servis alt sekmeleri yalnız ilgili kategoride görünür
    if (tab.conditional === 'İstek ve Şikayet/Ürün' && complaintCategory !== 'Ürün') {
      return false;
    }
    if (tab.conditional === 'İstek ve Şikayet/Teknik Servis' && complaintCategory !== 'Teknik Servis') {
      return false;
    }
    // İstek ve Şikayet seçildiyse mevcut akışın "ortası" gizlenir
    if (isIstekSikayet && ['device-select', 'checklist-after-device', 'fault-select', 'address-select', 'tech-service-select'].includes(tab.key)) {
      return false;
    }
    // Kurulum'da cihaz seçimi atlanır
    if (tab.key === 'device-select' && selectedOperation === 'Kurulum') {
      return false;
    }
    return true;
  });
  
  const activeTabIndex = visibleTabs.findIndex(tab => tab.key === activeTab);
  
  // Sekme değiştirme - kilitleme kontrolü ile
  const handleTabChange = (tabKey) => {
    if (finalSubmitting) return;
    
    // İzin verilen sekmeye kadar gidebilir
    const currentMaxIndex = visibleTabs.findIndex(tab => tab.key === maxAllowedTab);
    const targetIndex = visibleTabs.findIndex(tab => tab.key === tabKey);
    
    if (targetIndex <= currentMaxIndex) {
      // Geriye doğru veya aynı sekmeye gidebilir
      setActiveTab(tabKey);
    }
    // İleriye doğru gidilemez (devam butonuna basılmalı)
  };
  
  // Responsive breakpoints
  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth > 480 && windowWidth <= 768;
  const isDesktop = windowWidth > 768;

  // Aktif tab'ı window'a kaydet (breadcrumb için)
  useEffect(() => {
    const currentTab = visibleTabs.find(tab => tab.key === activeTab);
    if (currentTab) {
      window.activeServiceRecordTab = currentTab.label;
    }
    return () => {
      delete window.activeServiceRecordTab;
    };
  }, [activeTab, visibleTabs]);

  // Step pozisyonlarını hesapla ve progress bar'ı güncelle
  useEffect(() => {
    if (!isDesktop || !stepsContainerRef.current) {
      return;
    }

    const updateProgressBar = () => {
      const container = stepsContainerRef.current;
      if (!container) return;

      // Step container'larını bul
      const stepElements = container.querySelectorAll('[data-step-container]');
      if (stepElements.length === 0) return;

      const firstStep = stepElements[0];
      const lastStep = stepElements[stepElements.length - 1];
      
      if (!firstStep || !lastStep) return;

      const containerRect = container.getBoundingClientRect();
      const firstStepRect = firstStep.getBoundingClientRect();
      const lastStepRect = lastStep.getBoundingClientRect();

      // Step container'ların merkezini bul (circle'lar step container'ların merkezinde)
      const firstStepCenter = (firstStepRect.left + firstStepRect.width / 2) - containerRect.left;
      const lastStepCenter = (lastStepRect.left + lastStepRect.width / 2) - containerRect.left;
      
      const left = firstStepCenter;
      const right = containerRect.width - lastStepCenter;
      
      // Progress width hesapla
      const progressWidth = activeTabIndex > 0 
        ? (activeTabIndex / (visibleTabs.length - 1)) * 100 
        : 0;

      setProgressBarStyle({
        left: `${left}px`,
        right: `${right}px`,
        width: `${progressWidth}%`
      });
    };

    // Render sonrası güncelleme için timeout
    const timeoutId = setTimeout(updateProgressBar, 100);
    
    // Window resize'da güncelle
    window.addEventListener('resize', updateProgressBar);
    
    return () => {
      window.removeEventListener('resize', updateProgressBar);
      clearTimeout(timeoutId);
    };
  }, [activeTab, activeTabIndex, visibleTabs.length, isDesktop, selectedOperation]);

  return (
    <main className="main-content service-record-create" style={{ padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: 700, 
              color: 'var(--text-dark)', 
              marginBottom: '8px',
              letterSpacing: '-0.5px'
            }}>
              Servis Kaydı Oluştur
            </h1>
            <p style={{ 
              fontSize: '15px', 
              color: 'var(--text-gray)', 
              margin: 0 
            }}>
              Müşteri hizmetleri için servis kaydı oluşturun
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {gorusmeKaydiId ? (
              <button
                type="button"
                className="secondary-btn"
                onClick={handleGorusmeyiSonlandirClick}
                disabled={gorusmeSonlandirLoading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {gorusmeSonlandirLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-phone-slash"></i>}
                <span>Görüşmeyi sonlandır</span>
              </button>
            ) : null}
            {selectedOperation ? (
              <button
                type="button"
                className="secondary-btn"
                onClick={handleKaliciKaydet}
                disabled={kaliciKaydetLoading || !selectedCustomer}
                style={{ whiteSpace: 'nowrap' }}
                title="Kalıcı taslak olarak kaydet (tüm kullanıcılar görebilir)"
              >
                {kaliciKaydetLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                <span>{kaliciTaslakId ? 'Taslağı güncelle' : 'Taslak kaydet'}</span>
              </button>
            ) : null}
            <button
              type="button"
              className="secondary-btn"
              onClick={handleYeniBasla}
              style={{ whiteSpace: 'nowrap' }}
            >
              <i className="fas fa-redo"></i>
              <span>Yeni başla</span>
            </button>
          </div>
        </div>

        {/* Step Progress Bar */}
        <div style={{ 
          marginBottom: '40px',
          background: '#ffffff',
          borderRadius: '12px',
          padding: isDesktop ? '24px' : isTablet ? '16px' : '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div 
            ref={stepsContainerRef}
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: isDesktop ? 'space-between' : 'flex-start',
              position: 'relative',
              padding: isDesktop ? '0 20px' : '0 10px',
              flexWrap: 'wrap',
              gap: isDesktop ? '0' : '12px',
              overflowX: isDesktop ? 'visible' : 'auto',
              overflowY: 'hidden'
            }}
          >
            {/* Connection Lines - Desktop only */}
            {visibleTabs.length > 1 && isDesktop && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: progressBarStyle.left || '0px',
                right: progressBarStyle.right || '0px',
                height: '2px',
                background: 'var(--border-color)',
                zIndex: 0
              }}>
                <div style={{
                  height: '100%',
                  background: '#10b981',
                  width: progressBarStyle.width || '0%',
                  transition: 'width 0.3s ease, left 0.3s ease, right 0.3s ease'
                }}></div>
              </div>
            )}

            {/* Step Items */}
            {visibleTabs.map((tab, index) => {
              const isActive = activeTab === tab.key;
              const isCompleted = index < activeTabIndex;
              const displayStep = tab.key === 'gorusme-hub'
                ? 'S'
                : selectedOperation !== 'Arıza' && typeof tab.step === 'number' && tab.step > 3
                  ? tab.step - 1
                  : tab.step;

              const circleSize = isDesktop ? 40 : isTablet ? 32 : 28;
              const checkFontSize = isDesktop ? '16px' : isTablet ? '14px' : '12px';
              const stepFontSize = isDesktop ? '14px' : isTablet ? '12px' : '11px';
              const labelFontSize = isDesktop ? '13px' : isTablet ? '11px' : '10px';

              return (
                <div 
                  key={tab.key}
                  data-step-container
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: isDesktop ? 1 : '0 0 auto',
                    minWidth: isDesktop ? 'auto' : isTablet ? '70px' : '60px',
                    position: 'relative',
                    zIndex: 1,
                    cursor: (finalSubmitting || visibleTabs.findIndex(t => t.key === tab.key) > visibleTabs.findIndex(t => t.key === maxAllowedTab)) ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                    opacity: (finalSubmitting || visibleTabs.findIndex(t => t.key === tab.key) > visibleTabs.findIndex(t => t.key === maxAllowedTab)) ? 0.5 : 1
                  }}
                  onClick={() => !finalSubmitting && handleTabChange(tab.key)}
                >
                  {/* Step Circle */}
                  <div style={{
                    width: `${circleSize}px`,
                    height: `${circleSize}px`,
                    borderRadius: '50%',
                    background: isActive 
                      ? 'var(--primary-blue)' 
                      : isCompleted 
                        ? '#10b981' 
                        : '#ffffff',
                    border: `2px solid ${isActive 
                      ? 'var(--primary-blue)' 
                      : isCompleted 
                        ? '#10b981' 
                        : 'var(--border-color)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: isDesktop ? '12px' : '8px',
                    transition: 'all 0.3s ease',
                    boxShadow: isActive ? '0 4px 12px rgba(37, 99, 235, 0.3)' : 'none',
                    flexShrink: 0
                  }}>
                    {isCompleted ? (
                      <i className="fas fa-check" style={{ 
                        color: '#ffffff', 
                        fontSize: checkFontSize,
                        lineHeight: '1',
                        margin: 0,
                        padding: 0
                      }}></i>
                    ) : (
                      <span style={{
                        color: isActive ? '#ffffff' : 'var(--text-gray)',
                        fontWeight: 600,
                        fontSize: stepFontSize,
                        lineHeight: '1',
                        display: 'block',
                        margin: 0,
                        padding: 0
                      }}>
                        {displayStep}
                      </span>
                    )}
                  </div>

                  {/* Step Label */}
                  <span style={{
                    fontSize: labelFontSize,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'var(--primary-blue)' : 'var(--text-gray)',
                    textAlign: 'center',
                    maxWidth: isDesktop ? '100px' : isTablet ? '70px' : '60px',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {tab.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

      {/* Tab Content */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        minHeight: '400px'
      }}>
        {/* Tab 1: Müşteri Arama */}
        {activeTab === 'customer-search' && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '400px',
            width: '100%'
          }}>
            <div style={{ 
              width: '100%',
              maxWidth: '600px',
              marginBottom: '32px'
            }}>
              <div style={{ 
                marginBottom: '32px', 
                textAlign: 'center' 
              }}>
                <h2 style={{ 
                  fontSize: '22px', 
                  fontWeight: 600, 
                  color: 'var(--text-dark)',
                  marginBottom: '8px'
                }}>
                  Müşteri Arama
                </h2>
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--text-gray)' 
                }}>
                  Müşteriyi ad, soyad, telefon numarası veya cihaz seri numarası ile arayın
                </p>
              </div>
              
              <div className="search-box" style={{ position: 'relative', marginBottom: '16px', width: '80%', marginLeft: 'auto', marginRight: 'auto' }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-gray)' }}></i>
                <input
                  type="text"
                  placeholder="Ad, soyad, telefon veya cihaz seri numarası ile ara (min 3 karakter)..."
                  value={customerSearchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Sadece zararlı karakterleri temizle, boşlukları koru
                    const sanitized = value.replace(/[<>]/g, '');
                    setCustomerSearchInput(sanitized);
                  }}
                  maxLength={100}
                  autoComplete="off"
                  disabled={!!selectedCustomer}
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 48px',
                    fontSize: '15px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '10px',
                    background: selectedCustomer ? 'var(--bg-light)' : '#ffffff',
                    cursor: selectedCustomer ? 'not-allowed' : 'text',
                  opacity: selectedCustomer ? 0.6 : 1
                }}
              />
            </div>
              
              {/* Seçili Müşteri Kartı */}
              {selectedCustomer && (
                <div style={{
                  width: '80%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  marginBottom: '24px',
                  padding: '16px',
                  background: 'var(--bg-light)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-gray)', marginBottom: '4px' }}>
                      Seçili:
                    </div>
                    <div style={{ fontWeight: 500, fontSize: '15px', color: 'var(--text-dark)' }}>
                      {`${selectedCustomer.adi || selectedCustomer.name || ''} ${selectedCustomer.soyadi || selectedCustomer.surname || ''}`.trim() || '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDeselectCustomer}
                    style={{
                      marginLeft: '16px',
                      padding: '8px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-gray)',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = 'var(--error-red)';
                      e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = 'var(--text-gray)';
                      e.target.style.background = 'transparent';
                    }}
                    title="Müşteri seçimini iptal et"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}

              {!selectedCustomer && customerSearchResults.length > 0 && (
                <div className="search-results" style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  background: '#ffffff',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  marginBottom: '16px',
                  width: '80%',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  {customerSearchResults.map((customer) => (
                    <div
                      key={customer.id || customer._id}
                      role="button"
                      tabIndex={0}
                      data-testid={`customer-result-${(customer.id || customer._id || '').toString()}`}
                      aria-label={`Müşteri seç: ${`${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—'}`}
                      onClick={() => handleSelectCustomer(customer)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectCustomer(customer); } }}
                      style={{
                        padding: '16px',
                        borderBottom: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'var(--hover-bg)'}
                      onMouseLeave={(e) => e.target.style.background = '#ffffff'}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                        {`${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—'}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                        {customer.telefon || customer.phone || '—'}
                      </div>
                      {(customer.ilceAdi || customer.ilAdi) && (
                        <div style={{ fontSize: '14px', color: 'var(--text-gray)', marginTop: '4px' }}>
                          {[customer.ilceAdi, customer.ilAdi].filter(Boolean).join(' / ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {!selectedCustomer && customerSearchInput.trim().length >= 3 && !customerSearchLoading && customerSearchResults.length === 0 && (
                <div style={{
                  width: '80%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  textAlign: 'center',
                  padding: '24px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  background: 'var(--bg-light)',
                }}>
                  <div style={{ color: 'var(--text-gray)', marginBottom: (customerSearchInput.match(/\d/g) || []).length >= 5 ? '16px' : 0 }}>
                    Müşteri bulunamadı.
                  </div>
                  {(customerSearchInput.match(/\d/g) || []).length >= 5 ? (
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={handleGoToCreateNewCustomer}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <i className="fas fa-user-plus"></i>
                      Yeni Müşteri Oluştur
                    </button>
                  ) : (
                    <div style={{ fontSize: '13px', color: 'var(--text-gray)' }}>
                      Yeni müşteri eklemek için telefon numarası ile arayın (en az 5 rakam).
                    </div>
                  )}
                </div>
              )}

              {/* Devam Butonu */}
              <div style={{ 
                width: '80%', 
                marginLeft: 'auto', 
                marginRight: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '32px'
              }}>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => {
                    const nextTab = isSantralFlow ? 'gorusme-hub' : 'operation-select';
                    setActiveTab(nextTab);
                    setMaxAllowedTab(nextTab);
                  }}
                  disabled={!selectedCustomer}
                  style={{
                    opacity: selectedCustomer ? 1 : 0.5,
                    cursor: selectedCustomer ? 'pointer' : 'not-allowed'
                  }}
                >
                  <span>Devam</span>
                  <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Müşteri Oluştur */}
        {activeTab === 'customer-create' && (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ 
              marginBottom: '32px',
              textAlign: 'center' 
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 600, 
                color: 'var(--text-dark)',
                marginBottom: '8px'
              }}>
                Müşteri Oluştur
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-gray)' 
              }}>
                Yeni müşteri bilgilerini giriniz
              </p>
            </div>
            
            <form onSubmit={handleCreateNewCustomer}>
              <div className="form-grid">
                <label className="form-field">
                  <span>Ad *</span>
                  <input 
                    type="text" 
                    value={newCustomerForm.adi} 
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, adi: e.target.value })} 
                    maxLength={100} 
                    required 
                  />
                </label>
                <label className="form-field">
                  <span>Soyad *</span>
                  <input 
                    type="text" 
                    value={newCustomerForm.soyadi} 
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, soyadi: e.target.value })} 
                    maxLength={100} 
                    required 
                  />
                </label>
              </div>
              
              <div className="form-grid">
                <label className="form-field">
                  <span>Telefon *</span>
                  <input 
                    type="text" 
                    value={newCustomerForm.telefon} 
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, telefon: e.target.value })} 
                    maxLength={50} 
                    required 
                    disabled={phonePrefilledFromSearch}
                    title={phonePrefilledFromSearch ? 'Telefon arama ekranından geldi. Değiştirmek için önceki adıma dönün.' : undefined}
                    style={phonePrefilledFromSearch ? { backgroundColor: 'var(--bg-light)', cursor: 'not-allowed' } : undefined}
                  />
                </label>
                <label className="form-field">
                  <span>E-posta</span>
                  <input 
                    type="email" 
                    value={newCustomerForm.mail} 
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, mail: e.target.value })} 
                    maxLength={100} 
                  />
                </label>
              </div>
              
              <div className="form-grid">
                <label className="form-field">
                  <span>İl *</span>
                  <select
                    value={newCustomerForm.cityId}
                    onChange={(e) => {
                      setNewCustomerForm({ ...newCustomerForm, cityId: e.target.value, districtId: '' });
                      setIlceler([]);
                    }}
                    required
                    disabled={illerLoading}
                  >
                    <option value="">İl Seçiniz</option>
                    {iller.map((il) => (
                      <option key={il.id} value={String(il.id)}>
                        {il.name || il.ad}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-field">
                  <span>İlçe *</span>
                  <select
                    value={newCustomerForm.districtId}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, districtId: e.target.value })}
                    required
                    disabled={!newCustomerForm.cityId || ilcelerLoading}
                  >
                    <option value="">İlçe Seçiniz</option>
                    {ilceler.map((ilce) => (
                      <option key={ilce.id} value={String(ilce.id)}>
                        {ilce.name || ilce.ad}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              
              <div className="form-grid">
                <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                  <span>Adres *</span>
                  <textarea 
                    value={newCustomerForm.adres} 
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, adres: e.target.value })} 
                    rows="3"
                    maxLength={500} 
                    required 
                  />
                </label>
              </div>
              
              {newCustomerError && (
                <div className="error-state small" style={{ marginBottom: '16px' }}>
                  {safeText(newCustomerError)}
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => {
                    setActiveTab('customer-search');
                    setPhonePrefilledFromSearch(false);
                  }}
                  disabled={newCustomerSubmitting}
                >
                  Önceki Adım
                </button>
                <button 
                  type="submit" 
                  className="primary-btn" 
                  disabled={newCustomerSubmitting}
                >
                  {newCustomerSubmitting ? 'Oluşturuluyor...' : 'Müşteri Oluştur'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'gorusme-hub' && isSantralFlow && (
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <GorusmeHubStep
              musteriId={selectedCustomer?.id || selectedCustomer?._id}
              musteri={hubMusteriDetay || selectedCustomer}
              adresler={hubAdresler}
              acikKayitlar={hubAcikKayitlar}
              gecmisKayitlar={hubGecmisKayitlar}
              cihazlar={hubCihazlar}
              yukleniyor={hubYukleniyor}
              gorusmeler={hubGorusmeler}
              gorusmelerLoading={hubGorusmelerLoading}
              onLoadGorusmeler={loadHubGorusmeler}
              smsList={hubSms}
              smsLoading={hubSmsLoading}
              onLoadSms={loadHubSms}
              onKayitGoruntule={handleKayitGoruntule}
              onYeniKayit={handleHubYeniKayit}
            />
          </div>
        )}

        {/* Tab 3: İşlem Seç */}
        {activeTab === 'operation-select' && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '400px',
            width: '100%'
          }}>
            <div style={{ 
              width: '100%',
              maxWidth: '700px'
            }}>
              <div style={{ 
                marginBottom: '32px',
                textAlign: 'center' 
              }}>
                <h2 style={{ 
                  fontSize: '22px', 
                  fontWeight: 600, 
                  color: 'var(--text-dark)',
                  marginBottom: '8px'
                }}>
                  İşlem Tipi Seçin
                </h2>
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--text-gray)' 
                }}>
                  Yapılacak işlem tipini seçin
                </p>
              </div>

              {selectedCustomer?.iletisimIzni?.verildi !== true && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setIzinModalOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIzinModalOpen(true);
                    }
                  }}
                  style={{
                    marginBottom: '24px',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    color: '#3730a3',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    <i className="fas fa-shield-alt" style={{ marginRight: '8px' }}></i>
                    İletişim izni gerekli
                  </div>
                  Devam etmeden önce KVKK ve açık rıza onayı alınmalıdır.
                </div>
              )}

              {selectedCustomer?.odemeEksikSerhi?.aktif === true && (
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    color: '#92400e',
                    fontSize: '14px',
                    lineHeight: 1.5,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                    Ödeme eksikliği şerhi
                  </div>
                  Bu müşteride önceki randevuda alınamayan tahsilat kaydı bulunmaktadır. Yeni servis kaydı açarken müşterinin ödemeyi yapacağına dair beyan alınmalıdır.
                </div>
              )}
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : isTablet ? 'repeat(2, 1fr)' : '1fr',
                gap: '20px',
                marginBottom: '32px',
                width: '100%'
              }}>
                {OPERATION_TYPES.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    aria-label={`İşlem tipi: ${op.label}`}
                    onClick={() => {
                      setSelectedOperation(op.value);
                      // Kurulum'da cihaz seçimi yok; varsayılan "cihaz seçmek istemiyorum"
                      if (op.value === 'Kurulum') setDeviceNotSelected(true);
                      if (op.value === 'İstek ve Şikayet') {
                        setDeviceNotSelected(true);
                        setComplaintCategory('');
                        setComplaintTargetIcerikId('');
                        setComplaintTargetCihazId('');
                        setComplaintTargetTechServiceId('');
                        setComplaintTargetServisKaydiId('');
                        setComplaintNote('');
                        setIstekSikayetTuru('');
                      }
                    }}
                    style={{
                      padding: '32px 24px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px',
                      background: selectedOperation === op.value ? 'var(--primary-blue)' : '#ffffff',
                      color: selectedOperation === op.value ? '#ffffff' : 'var(--text-dark)',
                      border: `2px solid ${selectedOperation === op.value ? 'var(--primary-blue)' : 'var(--border-color)'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: selectedOperation === op.value ? '0 4px 12px rgba(37, 99, 235, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedOperation !== op.value) {
                        e.target.style.borderColor = 'var(--primary-blue)';
                        e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedOperation !== op.value) {
                        e.target.style.borderColor = 'var(--border-color)';
                        e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    <i className={`fas ${op.icon}`} style={{ fontSize: '40px' }}></i>
                    <span style={{ fontWeight: 600, fontSize: '16px' }}>{op.label}</span>
                  </button>
                ))}
              </div>

              {selectedCustomer?.odemeEksikSerhi?.aktif === true
                && selectedOperation
                && selectedOperation !== 'İstek ve Şikayet' && (
                <div
                  style={{
                    marginBottom: '24px',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #fcd34d',
                    background: '#fffbeb',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={odemeBeyaniChecked}
                      onChange={(e) => setOdemeBeyaniChecked(e.target.checked)}
                      style={{ marginTop: '3px' }}
                    />
                    <span style={{ fontSize: '14px', color: '#92400e', lineHeight: 1.5 }}>
                      <strong>Müşteri ödemeyi yapacağını beyan etti</strong>
                      <span style={{ display: 'block', fontWeight: 400, marginTop: '4px' }}>
                        Bu onay olmadan servis kaydı oluşturulamaz.
                      </span>
                    </span>
                  </label>
                </div>
              )}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                className="primary-btn"
                onClick={handleOperationDevam}
                disabled={!selectedOperation}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Tab: Ürün Seç (Kurulum) */}
        {activeTab === 'product-select' && selectedOperation === 'Kurulum' && (
          <div style={{ maxWidth: '920px', margin: '0 auto' }}>
            <div style={{ marginBottom: '32px', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', marginBottom: '16px' }}>
                <i className="fas fa-box-open" style={{ fontSize: '24px', color: '#2563eb' }}></i>
              </div>
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>Servise Gidilecek Ürün</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>Kurulum yapılacak ürünü katalogdan seçin.</p>
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>
                <i className="fas fa-cube" style={{ marginRight: '8px', color: '#2563eb' }}></i>
                Ürün Seçimi <span style={{ color: '#ef4444' }}>*</span>
              </label>
              {kurulumUrunlerLoading ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Ürün ve kategoriler yükleniyor...
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4b5563' }}>
                      <i className="fas fa-search" style={{ marginRight: '6px', color: '#9ca3af' }}></i>
                      Ürün adı veya kod ile ara
                    </label>
                    <input
                      type="text"
                      value={kurulumUrunArama}
                      onChange={(e) => setKurulumUrunArama(e.target.value)}
                      placeholder="Ürün adı veya kod"
                      maxLength={100}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        flexWrap: 'wrap',
                        gap: '8px',
                      }}
                    >
                      <label style={{ fontSize: '13px', fontWeight: 500, color: '#4b5563', margin: 0 }}>
                        <i className="fas fa-folder-tree" style={{ marginRight: '6px', color: '#9ca3af' }}></i>
                        Kategoriler ve ürünler
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => {
                            const all = {};
                            const walkOpen = (nodes) => {
                              nodes.forEach((n) => {
                                if (!n.visible) return;
                                const has =
                                  n.products.length > 0 || n.children.some((c) => c.visible);
                                if (has) all[n.cid] = true;
                                walkOpen(n.children);
                              });
                            };
                            walkOpen(kurulumAccTree);
                            setKurulumAccordionOpenIds(all);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '12px',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            color: '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          <i className="fas fa-folder-open" style={{ marginRight: '6px', fontSize: '13px' }} aria-hidden />
                          Tümünü aç
                        </button>
                        <button
                          type="button"
                          onClick={() => setKurulumAccordionOpenIds({})}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '12px',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            color: '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          <i className="fas fa-folder" style={{ marginRight: '6px', fontSize: '13px' }} aria-hidden />
                          Tümünü kapat
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        maxHeight: 'min(55vh, 520px)',
                        overflowY: 'auto',
                        padding: '4px',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        background: '#fafafa',
                      }}
                    >
                      {kurulumAccTree.filter((n) => n.visible).map((n) => renderKurulumAccNode(n, 0))}
                      {kurulumUrunlerKategorisiz.length > 0 && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e5e7eb' }}>
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#64748b',
                              marginBottom: 8,
                              paddingLeft: 4,
                            }}
                          >
                            Kategorisiz ürünler
                          </div>
                          {kurulumUrunlerKategorisiz.map((u) => {
                            const uid = u.id || u._id;
                            const sel = String(kurulumIcerikId) === String(uid);
                            const ad = u.icerikAdi || u.urunAdi || '—';
                            const kod = u.icerikKodu || u.urunKodu || '';
                            return (
                              <button
                                key={uid}
                                type="button"
                                onClick={() => kurulumUrunSecimde(String(uid))}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '12px 14px',
                                  marginBottom: 8,
                                  borderRadius: '8px',
                                  border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                                  background: sel ? '#eff6ff' : '#fff',
                                  cursor: 'pointer',
                                }}
                              >
                                <div style={{ fontWeight: sel ? 600 : 500, color: '#1f2937' }}>
                                  <FoldedSearchHighlight text={ad} aramaRaw={kurulumUrunAramaDebounced} />
                                </div>
                                {kod ? (
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                      marginTop: 4,
                                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    }}
                                  >
                                    <FoldedSearchHighlight text={kod} aramaRaw={kurulumUrunAramaDebounced} />
                                  </div>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {!kurulumUrunlerLoading && kurulumUrunlerFiltreli.length === 0 && (
                      <div
                        style={{
                          marginTop: '12px',
                          fontSize: '13px',
                          color: '#b45309',
                          background: '#fffbeb',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid #fde68a',
                        }}
                      >
                        <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                        Kriterlere uygun ürün yok. Aramayı gevşetin veya farklı kelimeler deneyin.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>
                <i className="fas fa-barcode" style={{ marginRight: '8px', color: '#6b7280' }}></i>
                Seri Numarası
                <span style={{ fontWeight: 400, fontSize: '12px', color: '#9ca3af', marginLeft: '8px' }}>(Opsiyonel)</span>
              </label>
              <input
                type="text"
                value={kurulumSeriNo}
                onChange={(e) => setKurulumSeriNo(e.target.value)}
                placeholder="Örn: SN123456"
                maxLength={100}
                style={{
                  width: '100%', maxWidth: '360px', padding: '12px 14px', borderRadius: '8px',
                  border: '1px solid #d1d5db', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setActiveTab('operation-select')}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => {
                  setActiveTab('checklist-questions');
                  setMaxAllowedTab('checklist-questions');
                }}
                disabled={!kurulumIcerikId}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* Tab: Kontrol Listesi (Kurulum / Arıza / Bakım) */}
        {((activeTab === 'checklist-questions' && selectedOperation === 'Kurulum')
          || (activeTab === 'checklist-after-device' && (selectedOperation === 'Arıza' || selectedOperation === 'Bakım'))) && (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ marginBottom: '32px', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', marginBottom: '16px' }}>
                <i className="fas fa-clipboard-check" style={{ fontSize: '24px', color: '#d97706' }}></i>
              </div>
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>Kontrol Listesi</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                Tüm sorulara <strong>Evet</strong> cevabı verilmelidir. Herhangi biri Hayır ise servis kaydı açılamaz.
              </p>
            </div>
            {checklistSablonLoading ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '20px', marginBottom: '8px', display: 'block' }}></i>
                Kontrol listesi yükleniyor...
              </div>
            ) : !(cagriMerkeziSorular.length > 0) ? (
              <div style={{ padding: '24px', background: '#f0fdf4', borderRadius: '12px', marginBottom: '24px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                <i className="fas fa-check-circle" style={{ color: '#16a34a', marginRight: '8px' }}></i>
                Bu ürün için kontrol listesi yok. Devam edebilirsiniz.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                {cagriMerkeziSorular.map((s, index) => {
                  const sid = String(s._id || s.id);
                  const cevap = checklistCevaplari[sid];
                  const isHayir = cevap === 'hayir';
                  const isEvet = cevap === 'evet';
                  const deger = checklistDegerleri[sid] || '';
                  return (
                    <div key={sid} style={{
                      padding: '20px', borderRadius: '12px',
                      border: isHayir ? '2px solid #fca5a5' : isEvet ? '2px solid #86efac' : '1px solid #e5e7eb',
                      background: isHayir ? '#fef2f2' : isEvet ? '#f0fdf4' : '#fff',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                          background: isHayir ? '#fecaca' : isEvet ? '#bbf7d0' : '#f3f4f6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 700, color: isHayir ? '#dc2626' : isEvet ? '#16a34a' : '#6b7280',
                        }}>
                          {isHayir ? <i className="fas fa-times"></i> : isEvet ? <i className="fas fa-check"></i> : (index + 1)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: '15px', color: '#1f2937', marginBottom: '12px' }}>{s.metin || '—'}</div>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                              type="button"
                              onClick={() => setChecklistCevaplari((prev) => ({ ...prev, [sid]: 'evet' }))}
                              style={{
                                padding: '8px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                fontWeight: 500, fontSize: '14px', transition: 'all 0.15s ease',
                                background: isEvet ? '#16a34a' : '#f3f4f6',
                                color: isEvet ? '#fff' : '#374151',
                              }}
                            >
                              <i className="fas fa-check" style={{ marginRight: '6px' }}></i>Evet
                            </button>
                            <button
                              type="button"
                              onClick={() => setChecklistCevaplari((prev) => ({ ...prev, [sid]: 'hayir' }))}
                              style={{
                                padding: '8px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                fontWeight: 500, fontSize: '14px', transition: 'all 0.15s ease',
                                background: isHayir ? '#dc2626' : '#f3f4f6',
                                color: isHayir ? '#fff' : '#374151',
                              }}
                            >
                              <i className="fas fa-times" style={{ marginRight: '6px' }}></i>Hayır
                            </button>
                          </div>
                          {s.degerAlani && isEvet && (
                            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={deger}
                                onChange={(e) => setChecklistDegerleri((prev) => ({ ...prev, [sid]: e.target.value }))}
                                placeholder="Değer"
                                style={{ maxWidth: '160px' }}
                              />
                              {s.degerBirimi ? (
                                <span style={{ fontSize: '14px', color: '#6b7280' }}>{s.degerBirimi}</span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                      {isHayir && (
                        <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fee2e2', borderRadius: '8px', fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="fas fa-exclamation-triangle"></i>
                          Kontrol listesi karşılanmadığı için servis kaydı açılamaz. Görüşmeyi sonlandırmanız gerekebilir.
                        </div>
                      )}
                    </div>
                  );
                })}
                {cagriMerkeziSorular.some((s) => checklistCevaplari[String(s._id || s.id)] === 'hayir') && (
                  <div style={{
                    padding: '16px 20px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#991b1b', fontWeight: 500 }}>
                      <i className="fas fa-ban"></i>
                      Kontrol listesi karşılanmadı. Devam edilemiyor.
                    </div>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={handleGorusmeyiSonlandirClick}
                      style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '8px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626' }}
                    >
                      <i className="fas fa-phone-slash" style={{ marginRight: '6px' }}></i>Görüşmeyi Sonlandır
                    </button>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  if (selectedOperation === 'Kurulum') {
                    setActiveTab('product-select');
                  } else {
                    setActiveTab('device-select');
                  }
                }}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={goAfterChecklistStep}
                disabled={
                  cagriMerkeziSorular.length > 0 &&
                  cagriMerkeziSorular.some((s) => {
                    const sid = String(s._id || s.id);
                    if (checklistCevaplari[sid] !== 'evet') return true;
                    if (s.degerAlani && !(checklistDegerleri[sid] || '').trim()) return true;
                    return false;
                  })
                }
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* Tab 5: Arıza Seç */}
        {activeTab === 'fault-select' && selectedOperation === 'Arıza' && (
          <div>
            <div style={{ 
              marginBottom: '32px',
              textAlign: 'center' 
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 600, 
                color: 'var(--text-dark)',
                marginBottom: '8px'
              }}>
                Arıza Seçimi
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-gray)' 
              }}>
                Arızaları seçmek için "Arıza Ekle" butonuna tıklayın
              </p>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
              <button
                type="button"
                className="primary-btn"
                onClick={() => setIsFaultSearchModalOpen(true)}
              >
                <i className="fas fa-plus"></i>
                <span>Arıza Ekle</span>
              </button>
            </div>
            
            {/* Eklenen Arızalar */}
            <div style={{ marginBottom: '24px' }}>
              <div className="section-title" style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>
                Eklenen Arızalar
              </div>
              {selectedFaults.length === 0 ? (
                <div style={{ 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '10px', 
                  padding: '24px',
                  background: 'var(--bg-light)',
                  textAlign: 'center',
                  color: 'var(--text-gray)'
                }}>
                  Henüz arıza eklenmedi. Arıza eklemek için yukarıdaki "Arıza Ekle" butonuna tıklayın.
                </div>
              ) : (
                <div style={{ 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '10px', 
                  padding: '16px',
                  background: 'var(--bg-light)'
                }}>
                  {selectedFaults.map((fault) => {
                    const faultId = fault.id || fault._id;
                    return (
                      <div 
                        key={faultId} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '12px',
                          background: '#ffffff',
                          borderRadius: '6px',
                          marginBottom: '8px'
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>{fault.icerikAdi || fault.arizaAdi || fault.ad || fault.kategoriAdi || '—'}</span>
                        <button
                          type="button"
                          className="category-action-btn delete-btn"
                          onClick={() => handleRemoveFault(faultId)}
                          title="Kaldır"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Arıza Notu */}
            <div style={{ marginBottom: '24px' }}>
              <label className="form-field">
                <span>Arıza Notu</span>
                <textarea 
                  value={faultNote}
                  onChange={(e) => setFaultNote(e.target.value)}
                  rows="4"
                  maxLength={500}
                  placeholder="Seçili arızalar ile ilgili notlarınızı buraya yazabilirsiniz..."
                  style={{ width: '100%' }}
                />
              </label>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  const urunId = resolveChecklistUrunIcerikId();
                  setActiveTab(urunId ? 'checklist-after-device' : 'device-select');
                }}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => {
                  setActiveTab('address-select');
                  setMaxAllowedTab('address-select');
                }}
                disabled={selectedFaults.length === 0 && !faultNote.trim()}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* Tab 4: Cihaz Seç */}
        {activeTab === 'device-select' && (
          <div>
            <div style={{ 
              marginBottom: '32px',
              textAlign: 'center' 
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 600, 
                color: 'var(--text-dark)',
                marginBottom: '8px'
              }}>
                Cihaz Seçimi
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-gray)' 
              }}>
                {selectedOperation === 'Bakım'
                  ? 'Bakım yapılacak cihazları seçin (birden fazla seçilebilir)'
                  : 'Arızalı cihazı seçin (yalnızca bir cihaz)'}
              </p>
            </div>
            
            {devicesLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <div className="loading-text">Cihazlar yükleniyor...</div>
              </div>
            ) : devices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-gray)' }}>
                Bu müşteriye ait cihaz bulunmamaktadır.
              </div>
            ) : (
              <div className="table-container" style={{ marginBottom: '24px' }}>
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Seç</th>
                      <th>Ürün Adı</th>
                      <th>Seri Numarası</th>
                      <th>Garanti Süresi (Ay)</th>
                      {selectedOperation === 'Bakım' && <th style={{ textAlign: 'right' }}>Bakım Fiyatı</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device) => {
                      const deviceId = device.id || device._id;
                      const isSelected = selectedDevices.includes(deviceId);
                      const isAriza = selectedOperation === 'Arıza';
                      
                      return (
                        <tr key={deviceId}>
                          <td>
                            <input
                              type={isAriza ? 'radio' : 'checkbox'}
                              name={isAriza ? 'ariza-device-select' : undefined}
                              checked={isSelected}
                              onChange={() => handleDeviceToggle(deviceId)}
                              disabled={deviceNotSelected}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: deviceNotSelected ? 'not-allowed' : 'pointer',
                              }}
                            />
                          </td>
                          <td>{device.urun?.urunAdi || device.icerik?.icerikAdi || '—'}</td>
                          <td>{device.seriNumarasi || '—'}</td>
                          <td>{device.garantiSuresi || '—'}</td>
                          {selectedOperation === 'Bakım' && (
                            <td style={{ textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                              {getDeviceBakimFiyatLabel(device)}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            
            <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-light)', borderRadius: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={deviceNotSelected}
                  onChange={(e) => handleDeviceNotSelectedToggle(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: 500 }}>Cihaz seçmek istemiyorum</span>
              </label>
              
              {deviceNotSelected && (
                <div style={{ marginTop: '16px' }}>
                  <label className="form-field">
                    <span>Açıklama</span>
                    <textarea 
                      value={deviceExplanation}
                      onChange={(e) => setDeviceExplanation(e.target.value)}
                      rows="3"
                      maxLength={500}
                      placeholder="Bu konuyla ilgili açıklama giriniz..."
                      style={{ width: '100%' }}
                    />
                  </label>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setActiveTab('operation-select');
                }}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={goAfterDeviceSelect}
                disabled={!deviceNotSelected && selectedDevices.length === 0}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* Tab 6: Adres Seç */}
        {activeTab === 'address-select' && (
          <div>
            <div style={{ 
              marginBottom: '32px',
              textAlign: 'center' 
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 600, 
                color: 'var(--text-dark)',
                marginBottom: '8px'
              }}>
                Adres Seçimi
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-gray)' 
              }}>
                Servis için adres seçin veya yeni adres ekleyin
              </p>
            </div>
            
            {addressesLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <div className="loading-text">Adresler yükleniyor...</div>
              </div>
            ) : addresses.length === 0 ? (
              <div className="empty-state" style={{
                padding: '40px 20px',
                textAlign: 'center',
                background: '#f8fafc',
                borderRadius: '10px',
                border: '1px dashed #cbd5e1',
                marginBottom: '24px'
              }}>
                <i className="fas fa-map-marker-alt" style={{ fontSize: '32px', color: '#94a3b8', marginBottom: '12px' }}></i>
                <div style={{ fontSize: '16px', fontWeight: 500, color: '#1e293b', marginBottom: '8px' }}>
                  Henüz adres eklenmemiş
                </div>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                  Bu müşteri için henüz kayıtlı adres bulunmamaktadır. Yeni adres eklemek için aşağıdaki butona tıklayın.
                </div>
              </div>
            ) : (
              <>
                <div className="table-container" style={{ marginBottom: '24px' }}>
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Seç</th>
                        <th>Adres</th>
                        <th>Varsayılan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addresses.map((address) => {
                        const addressId = address.id || address._id;
                        const isSelected = selectedAddress && (selectedAddress.id || selectedAddress._id) === addressId;
                        
                        return (
                          <tr key={addressId}>
                            <td>
                              <input
                                type="radio"
                                name="address"
                                checked={isSelected}
                                onChange={() => handleSelectAddress(address)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                            </td>
                            <td>{address.adres || '—'}</td>
                            <td>
                              {address.varsayilan ? (
                                <span style={{ color: 'var(--success-green)', fontWeight: 500 }}>Varsayılan</span>
                              ) : (
                                <span style={{ color: 'var(--text-gray)' }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setIsAddressModalOpen(true)}
                  >
                    <i className="fas fa-plus"></i>
                    <span>Yeni Adres Ekle</span>
                  </button>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      setActiveTab(getPreviousTabFromAddress());
                    }}
                  >
                    <i className="fas fa-arrow-left"></i>
                    <span>Geri</span>
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      setActiveTab('tech-service-select');
                      setMaxAllowedTab('tech-service-select');
                    }}
                    disabled={!selectedAddress}
                  >
                    <span>Devam</span>
                    <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab 7: Teknik Servis Seç */}
        {activeTab === 'tech-service-select' && (
          <div>
            <div style={{ 
              marginBottom: '32px',
              textAlign: 'center' 
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 600, 
                color: 'var(--text-dark)',
                marginBottom: '8px'
              }}>
                Teknik Servis Seçimi
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-gray)' 
              }}>
                Servis için teknik servis seçin
              </p>
            </div>

            {techServicesLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <div className="loading-text">Teknik servisler yükleniyor...</div>
              </div>
            ) : (
              <>
                {/* Otomatik seçilen teknik servisler */}
                {selectedTechServices.length > 0 && (
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    background: '#eff6ff',
                    border: '2px solid #3b82f6',
                    borderRadius: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <i className="fas fa-check-circle" style={{ color: '#3b82f6', fontSize: '20px' }}></i>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                        Seçilen Teknik Servis
                      </div>
                    </div>
                    <div style={{ marginLeft: '32px' }}>
                      {(() => {
                        const service = selectedTechServices[0];
                        if (!service) return null;
                        return (
                          <div style={{ fontSize: '14px', color: '#64748b' }}>
                            {service.ad || service.name || 'Teknik Servis'}
                            {service.kod && ` (${service.kod})`}
                            <button
                              type="button"
                              onClick={() => setSelectedTechServices([])}
                              style={{
                                marginLeft: '8px',
                                background: 'transparent',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="Kaldır"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Teknik servis arama */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                    Farklı Teknik Servis Seç
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={techServiceSearchInput}
                      onChange={(e) => setTechServiceSearchInput(e.target.value)}
                      placeholder="Ad, kod veya il — en az 2 karakter (Türkçe destekli arama)"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                    {techServiceSearchLoading && (
                      <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="loading-spinner" style={{ width: '20px', height: '20px' }}></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Arama sonuçları */}
                  {techServiceSearchInput && techServiceSearchInput.trim().length >= 2 && (
                    <div style={{
                      marginTop: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      background: '#ffffff'
                    }}>
                      {techServiceSearchLoading && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                          Aranıyor...
                        </div>
                      )}
                      {!techServiceSearchLoading && techServiceSearchResults.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                          Sonuç bulunamadı
                        </div>
                      )}
                      {!techServiceSearchLoading && techServiceSearchResults.map((service) => {
                        const serviceId = service.id || service._id;
                        const isSelected = selectedTechServices.some(s => (s.id || s._id) === serviceId);
                        
                        return (
                          <div
                            key={serviceId}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedTechServices([]);
                              } else {
                                setSelectedTechServices([service]);
                              }
                            }}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              background: isSelected ? '#eff6ff' : '#ffffff',
                              transition: 'background 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.background = '#f8fafc';
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) e.currentTarget.style.background = '#ffffff';
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500, fontSize: '14px', color: '#1e293b' }}>
                                <FoldedSearchHighlight
                                  text={service.ad || service.name || 'Teknik Servis'}
                                  queryRaw={techServiceSearchInput}
                                />
                                {service.kod != null && service.kod !== '' ? (
                                  <span style={{ color: '#64748b', marginLeft: '8px' }}>
                                    (
                                    <FoldedSearchHighlight text={String(service.kod)} queryRaw={techServiceSearchInput} />
                                    )
                                  </span>
                                ) : null}
                              </div>
                              {(() => {
                                // Konum bilgisini güvenli şekilde göster
                                let konumText = '';
                                if (service.konum) {
                                  if (typeof service.konum === 'string') {
                                    konumText = service.konum;
                                  } else if (typeof service.konum === 'object') {
                                    // Obje ise adres veya name alanını kullan
                                    konumText = service.konum.adres || service.konum.name || service.konum.ad || '';
                                  }
                                }
                                // Alternatif olarak il/ilçe bilgisini göster
                                if (!konumText && (service.il || service.ilce)) {
                                  const ilAd = service.il?.name || service.il?.ad || '';
                                  const ilceAd = service.ilce?.name || service.ilce?.ad || '';
                                  if (ilAd && ilceAd) {
                                    konumText = `${ilceAd} / ${ilAd}`;
                                  } else if (ilAd) {
                                    konumText = ilAd;
                                  } else if (ilceAd) {
                                    konumText = ilceAd;
                                  }
                                }
                                return konumText ? (
                                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                    {konumText}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                            {isSelected && (
                              <i className="fas fa-check-circle" style={{ color: '#3b82f6', fontSize: '18px' }}></i>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      setActiveTab('address-select');
                    }}
                  >
                    <i className="fas fa-arrow-left"></i>
                    <span>Geri</span>
                  </button>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => {
                      setActiveTab('finish');
                      setMaxAllowedTab('finish');
                    }}
                    disabled={selectedTechServices.length === 0}
                  >
                    <span>Devam</span>
                    <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* İstek ve Şikayet — Kategori Seç */}
        {activeTab === 'complaint-category-select' && (
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ marginBottom: '32px', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', marginBottom: '16px' }}>
                <i className="fas fa-tags" style={{ fontSize: '24px', color: '#d97706' }}></i>
              </div>
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>İstek/Şikayet Kategorisi</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>İstek veya şikayetin ilgili olduğu kategoriyi seçin.</p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : isTablet ? 'repeat(2, 1fr)' : '1fr',
              gap: '16px',
              marginBottom: '24px',
            }}>
              {COMPLAINT_CATEGORIES.map((cat) => {
                const sel = complaintCategory === cat.value;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => {
                      setComplaintCategory(cat.value);
                      setComplaintTargetIcerikId('');
                      setComplaintTargetCihazId('');
                      setComplaintTargetTechServiceId('');
                      setComplaintTargetServisKaydiId('');
                    }}
                    style={{
                      padding: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      background: sel ? 'var(--primary-blue)' : '#fff',
                      color: sel ? '#fff' : 'var(--text-dark)',
                      border: `2px solid ${sel ? 'var(--primary-blue)' : 'var(--border-color)'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: sel ? '0 4px 12px rgba(37, 99, 235, 0.25)' : '0 1px 3px rgba(0,0,0,0.05)',
                      minHeight: '120px',
                    }}
                  >
                    <i className={`fas ${cat.icon}`} style={{ fontSize: '32px' }}></i>
                    <span style={{ fontWeight: 600, fontSize: '15px', textAlign: 'center' }}>{cat.label}</span>
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setActiveTab('operation-select')}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={!complaintCategory}
                onClick={() => {
                  let nextTab = 'complaint-note';
                  if (complaintCategory === 'Ürün') nextTab = 'complaint-product-select';
                  else if (complaintCategory === 'Teknik Servis') nextTab = 'complaint-tech-service-select';
                  setActiveTab(nextTab);
                  setMaxAllowedTab(nextTab);
                }}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* İstek ve Şikayet — Ürün adımı */}
        {activeTab === 'complaint-product-select' && complaintCategory === 'Ürün' && (
          <div style={{ maxWidth: '960px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>Ürün Seçimi</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                Şikayet ettiğiniz ürünü kategorilerden seçin veya müşteriye kayıtlı bir cihazı işaret edin.
              </p>
            </div>

            {/* Müşteri cihazları tablosu */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <i className="fas fa-microchip" style={{ color: '#0ea5e9' }}></i>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Müşterinin Cihazları</h3>
              </div>
              {devicesLoading ? (
                <div style={{ padding: '12px', color: '#64748b' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
                </div>
              ) : devices.length === 0 ? (
                <div style={{ padding: '12px', color: '#94a3b8', fontStyle: 'italic' }}>Bu müşteriye ait kayıtlı cihaz yok.</div>
              ) : (
                <div className="table-container">
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Seç</th>
                        <th>Cihaz</th>
                        <th>Seri No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.map((d) => {
                        const id = d.id || d._id;
                        const ad = d.urun?.icerikAdi || d.urun?.urunAdi || d.icerik?.icerikAdi || d.icerik?.urunAdi || d.urunAdi || d.icerikAdi || 'Cihaz';
                        const sel = String(complaintTargetCihazId) === String(id);
                        return (
                          <tr key={id}>
                            <td>
                              <input
                                type="radio"
                                name="complaint-product-cihaz"
                                checked={sel}
                                onChange={() => {
                                  setComplaintTargetCihazId(id);
                                  setComplaintTargetIcerikId('');
                                }}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                            </td>
                            <td>{safeText(ad)}</td>
                            <td>{safeText(d.seriNumarasi || d.seriNo || '—')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Ürün akordeon ağacı + arama (Kurulum'daki ile aynı) */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <i className="fas fa-cube" style={{ color: '#2563eb' }}></i>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Ürün Kataloğu</h3>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  value={kurulumUrunArama}
                  onChange={(e) => setKurulumUrunArama(e.target.value)}
                  placeholder="Ürün adı veya kod"
                  maxLength={100}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              {kurulumUrunlerLoading ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Ürün ve kategoriler yükleniyor...
                </div>
              ) : (
                <div style={{ maxHeight: 'min(50vh, 480px)', overflowY: 'auto', padding: 4, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fafafa' }}>
                  {kurulumAccTree.filter((n) => n.visible).map((n) => renderKurulumAccNode(n, 0, {
                    selectedId: complaintTargetIcerikId,
                    onSelectId: (id) => {
                      setComplaintTargetIcerikId(String(id));
                      setComplaintTargetCihazId('');
                    },
                  }))}
                  {kurulumUrunlerKategorisiz.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e5e7eb' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, paddingLeft: 4 }}>Kategorisiz ürünler</div>
                      {kurulumUrunlerKategorisiz.map((u) => {
                        const uid = u.id || u._id;
                        const sel = String(complaintTargetIcerikId) === String(uid);
                        const ad = u.icerikAdi || u.urunAdi || '—';
                        const kod = u.icerikKodu || u.urunKodu || '';
                        return (
                          <button
                            key={uid}
                            type="button"
                            onClick={() => {
                              setComplaintTargetIcerikId(String(uid));
                              setComplaintTargetCihazId('');
                            }}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: 8,
                              borderRadius: 8, border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb', background: sel ? '#eff6ff' : '#fff', cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontWeight: sel ? 600 : 500, color: '#1f2937' }}>{safeText(ad)}</div>
                            {kod ? <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{safeText(kod)}</div> : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              Not: Ürün kataloğundan veya müşteri cihazlarından <strong>yalnızca biri</strong> seçilebilir; her ikisi de boş bırakılabilir (kategori temelli şikayet için).
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setActiveTab('complaint-category-select')}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => {
                  setActiveTab('complaint-note');
                  setMaxAllowedTab('complaint-note');
                }}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* İstek ve Şikayet — Teknik Servis adımı */}
        {activeTab === 'complaint-tech-service-select' && complaintCategory === 'Teknik Servis' && (
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>Teknik Servis Seçimi</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                Şikayet ettiğiniz teknik servisi seçin. İl ile arama daraltılabilir; satır yanındaki info butonu detayları gösterir.
              </p>
            </div>

            {/* Filtreler */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '16px', display: 'grid', gap: 12, gridTemplateColumns: isDesktop ? '200px 1fr' : '1fr' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#475569' }}>İl</label>
                <select
                  value={complaintCityId}
                  onChange={(e) => setComplaintCityId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
                >
                  <option value="">Tüm iller</option>
                  {iller.map((il) => {
                    const cid = il.id || il._id;
                    const ad = il.name || il.ad;
                    return (
                      <option key={cid} value={cid}>
                        {ad}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#475569' }}>Teknik Servis Ara</label>
                <input
                  type="text"
                  value={complaintTsSearchInput}
                  onChange={(e) => setComplaintTsSearchInput(e.target.value)}
                  placeholder="Ad, telefon veya e-posta (en az 2 karakter)"
                  maxLength={100}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Teknik servis listesi */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <i className="fas fa-screwdriver-wrench" style={{ color: '#2563eb' }}></i>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Teknik Servisler</h3>
              </div>
              {complaintTsLoading ? (
                <div style={{ padding: 16, color: '#64748b' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Aranıyor...
                </div>
              ) : complaintTsResults.length === 0 ? (
                <div style={{ padding: 16, color: '#94a3b8', fontStyle: 'italic' }}>Sonuç bulunamadı.</div>
              ) : (
                <div className="table-container" style={{ maxHeight: 360, overflowY: 'auto' }}>
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Seç</th>
                        <th>Ad</th>
                        <th>İl / İlçe</th>
                        <th>Telefon</th>
                        <th style={{ width: 60 }}>Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {complaintTsResults.map((ts) => {
                        const id = ts.id || ts._id;
                        const ilAd = ts.il?.name || ts.il?.ad || '';
                        const ilceAd = ts.ilce?.name || ts.ilce?.ad || '';
                        const sel = String(complaintTargetTechServiceId) === String(id);
                        return (
                          <tr key={id}>
                            <td>
                              <input
                                type="radio"
                                name="complaint-ts"
                                checked={sel}
                                onChange={() => setComplaintTargetTechServiceId(id)}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, color: '#1f2937' }}>
                                <FoldedSearchHighlight text={ts.ad || ts.name || ''} queryRaw={complaintTsSearchInput} />
                              </div>
                              {ts.kod != null && ts.kod !== '' ? (
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                  Kod:{' '}
                                  <FoldedSearchHighlight text={String(ts.kod)} queryRaw={complaintTsSearchInput} />
                                </div>
                              ) : null}
                            </td>
                            <td>{safeText([ilceAd, ilAd].filter(Boolean).join(' / ') || '—')}</td>
                            <td>{safeText(ts.telefon || '—')}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => setOpenTechServiceInfoId(id)}
                                title="Teknik servis bilgileri"
                                style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#2563eb' }}
                              >
                                <i className="fas fa-info-circle"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Müşteri cihazları */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <i className="fas fa-microchip" style={{ color: '#0ea5e9' }}></i>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Müşterinin Cihazları</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>(opsiyonel — şikayetin ilgili olduğu cihazı seçebilirsiniz)</span>
              </div>
              {devicesLoading ? (
                <div style={{ padding: 12, color: '#64748b' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
                </div>
              ) : devices.length === 0 ? (
                <div style={{ padding: 12, color: '#94a3b8', fontStyle: 'italic' }}>Bu müşteriye ait kayıtlı cihaz yok.</div>
              ) : (
                <div className="table-container">
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Seç</th>
                        <th>Cihaz</th>
                        <th>Seri No</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="radio"
                            name="complaint-ts-cihaz"
                            checked={!complaintTargetCihazId}
                            onChange={() => setComplaintTargetCihazId('')}
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                        </td>
                        <td colSpan={2} style={{ color: '#64748b', fontStyle: 'italic' }}>Cihaz seçimi yok</td>
                      </tr>
                      {devices.map((d) => {
                        const id = d.id || d._id;
                        const ad = d.urun?.icerikAdi || d.urun?.urunAdi || d.icerik?.icerikAdi || d.icerik?.urunAdi || d.urunAdi || d.icerikAdi || 'Cihaz';
                        const sel = String(complaintTargetCihazId) === String(id);
                        return (
                          <tr key={id}>
                            <td>
                              <input
                                type="radio"
                                name="complaint-ts-cihaz"
                                checked={sel}
                                onChange={() => setComplaintTargetCihazId(id)}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                            </td>
                            <td>{safeText(ad)}</td>
                            <td>{safeText(d.seriNumarasi || d.seriNo || '—')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Müşteri servis kayıtları */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <i className="fas fa-clipboard-list" style={{ color: '#10b981' }}></i>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Müşterinin Servis Kayıtları</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>(opsiyonel — info butonu ile kayıt detayını/randevuları inceleyebilirsiniz)</span>
              </div>
              {complaintMusteriServisKayitlariLoading ? (
                <div style={{ padding: 12, color: '#64748b' }}>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
                </div>
              ) : complaintMusteriServisKayitlari.length === 0 ? (
                <div style={{ padding: 12, color: '#94a3b8', fontStyle: 'italic' }}>Bu müşteriye ait önceki servis kaydı yok.</div>
              ) : (
                <div className="table-container" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Seç</th>
                        <th>Servis No</th>
                        <th>İşlem Tipi</th>
                        <th>Durum</th>
                        <th>Tarih</th>
                        <th style={{ width: 60 }}>Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="radio"
                            name="complaint-ts-sk"
                            checked={!complaintTargetServisKaydiId}
                            onChange={() => setComplaintTargetServisKaydiId('')}
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                        </td>
                        <td colSpan={5} style={{ color: '#64748b', fontStyle: 'italic' }}>Servis kaydı seçimi yok</td>
                      </tr>
                      {complaintMusteriServisKayitlari.map((sk) => {
                        const id = sk.id || sk._id;
                        const sel = String(complaintTargetServisKaydiId) === String(id);
                        const tarih = sk.olusturmaTarihi || sk.createdAt;
                        return (
                          <tr key={id}>
                            <td>
                              <input
                                type="radio"
                                name="complaint-ts-sk"
                                checked={sel}
                                onChange={() => setComplaintTargetServisKaydiId(id)}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                            </td>
                            <td>{safeText(sk.numara || '—')}</td>
                            <td>{safeText(sk.islemTipi || '—')}</td>
                            <td>
                              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: '#e0f2fe', color: '#075985' }}>
                                {safeText(sk.durum || '—')}
                              </span>
                            </td>
                            <td>{tarih ? new Date(tarih).toLocaleDateString('tr-TR') : '—'}</td>
                            <td>
                              <button
                                type="button"
                                onClick={() => setOpenMusteriServisKaydiId(id)}
                                title="Servis kaydı detayı"
                                style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#2563eb' }}
                              >
                                <i className="fas fa-info-circle"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setActiveTab('complaint-category-select')}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => {
                  setActiveTab('complaint-note');
                  setMaxAllowedTab('complaint-note');
                }}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* İstek ve Şikayet — Not adımı */}
        {activeTab === 'complaint-note' && (
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '8px' }}>İstek / Şikayet Notu</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                Önce kaydın <strong>İstek</strong> mi yoksa <strong>Şikayet</strong> mi olduğunu seçin; ardından açıklama ve önem derecesini doldurun.
              </p>
            </div>

            <div style={{ marginBottom: 20, padding: '16px 18px', background: 'var(--bg-light)', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '12px' }}>
                Kayıt türü <span style={{ color: '#dc2626' }}>*</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '15px', color: 'var(--text-dark)' }}>
                  <input
                    type="radio"
                    name="istek-sikayet-turu"
                    value="İstek"
                    checked={istekSikayetTuru === 'İstek'}
                    onChange={() => setIstekSikayetTuru('İstek')}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--primary-blue)' }}
                  />
                  <span><i className="fas fa-hand-paper" style={{ marginRight: 6, color: '#2563eb' }}></i>İstek</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '15px', color: 'var(--text-dark)' }}>
                  <input
                    type="radio"
                    name="istek-sikayet-turu"
                    value="Şikayet"
                    checked={istekSikayetTuru === 'Şikayet'}
                    onChange={() => setIstekSikayetTuru('Şikayet')}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--primary-blue)' }}
                  />
                  <span><i className="fas fa-exclamation-circle" style={{ marginRight: 6, color: '#d97706' }}></i>Şikayet</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="form-field">
                <span>Açıklama *</span>
                <textarea
                  value={complaintNote}
                  onChange={(e) => setComplaintNote(e.target.value.substring(0, 2000))}
                  rows="8"
                  maxLength={2000}
                  placeholder={istekSikayetTuru === 'Şikayet' ? 'Şikâyetinizi detaylı yazın…' : istekSikayetTuru === 'İstek' ? 'İsteğinizi detaylı yazın…' : 'Önce yukarıdan İstek veya Şikayet seçin, sonra açıklamanızı yazın…'}
                />
                <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8' }}>
                  {complaintNote.length} / 2000
                </div>
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  if (complaintCategory === 'Ürün') setActiveTab('complaint-product-select');
                  else if (complaintCategory === 'Teknik Servis') setActiveTab('complaint-tech-service-select');
                  else setActiveTab('complaint-category-select');
                }}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={!complaintNote.trim() || !istekSikayetTuru}
                onClick={() => {
                  setActiveTab('finish');
                  setMaxAllowedTab('finish');
                }}
              >
                <span>Devam</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {/* Tab 8: Bitir */}
        {activeTab === 'finish' && (
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ 
              marginBottom: '32px',
              textAlign: 'center' 
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 600, 
                color: 'var(--text-dark)',
                marginBottom: '8px'
              }}>
                Servis Kaydı Özeti
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--text-gray)' 
              }}>
                Son kontrolleri yapın ve servis kaydını oluşturun
              </p>
            </div>
            
            {/* Özet Bilgiler */}
            <div style={{ 
              background: 'var(--bg-light)', 
              padding: '20px', 
              borderRadius: '10px', 
              marginBottom: '24px' 
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Müşteri:</strong> {selectedCustomer ? `${selectedCustomer.adi || ''} ${selectedCustomer.soyadi || ''}`.trim() : '—'}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>İşlem Tipi:</strong> {selectedOperation || '—'}
              </div>
              {isIstekSikayet && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Kategori:</strong> {complaintCategory || '—'}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Kayıt türü:</strong>{' '}
                    {istekSikayetTuru === 'İstek' ? (
                      <span style={{ color: '#2563eb', fontWeight: 600 }}>İstek</span>
                    ) : istekSikayetTuru === 'Şikayet' ? (
                      <span style={{ color: '#b45309', fontWeight: 600 }}>Şikayet</span>
                    ) : (
                      '—'
                    )}
                  </div>
                  {complaintCategory === 'Ürün' && (complaintTargetIcerikId || complaintTargetCihazId) && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Hedef:</strong>{' '}
                      {complaintTargetIcerikId ? (() => {
                        const u = (kurulumUrunler || []).find((x) => String(x.id || x._id) === String(complaintTargetIcerikId));
                        const ad = u?.icerikAdi || u?.urunAdi || '';
                        const kod = u?.icerikKodu || u?.urunKodu || '';
                        return safeText([ad, kod ? `(${kod})` : ''].filter(Boolean).join(' '));
                      })() : (() => {
                        const d = (devices || []).find((x) => String(x.id || x._id) === String(complaintTargetCihazId));
                        const ad = d?.urun?.icerikAdi || d?.urun?.urunAdi || d?.icerik?.icerikAdi || d?.icerik?.urunAdi || d?.urunAdi || d?.icerikAdi || 'Cihaz';
                        return `Cihaz: ${safeText(ad)}${d?.seriNumarasi ? ` (S/N: ${safeText(d.seriNumarasi)})` : ''}`;
                      })()}
                    </div>
                  )}
                  {complaintCategory === 'Teknik Servis' && (complaintTargetTechServiceId || complaintTargetServisKaydiId || complaintTargetCihazId) && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Hedef:</strong>
                      <div style={{ marginTop: 4, fontSize: 14, color: 'var(--text-dark)' }}>
                        {complaintTargetTechServiceId ? (() => {
                          const ts = (complaintTsResults || []).find((x) => String(x.id || x._id) === String(complaintTargetTechServiceId));
                          return ts ? `Teknik Servis: ${safeText(ts.ad || ts.name || '')}${ts.kod ? ` (${ts.kod})` : ''}` : '';
                        })() : null}
                        {complaintTargetServisKaydiId ? (() => {
                          const sk = (complaintMusteriServisKayitlari || []).find((x) => String(x.id || x._id) === String(complaintTargetServisKaydiId));
                          return sk ? <div>Servis Kaydı: {safeText(sk.numara || sk._id || '')}</div> : null;
                        })() : null}
                        {complaintTargetCihazId ? (() => {
                          const d = (devices || []).find((x) => String(x.id || x._id) === String(complaintTargetCihazId));
                          const ad = d?.urun?.icerikAdi || d?.urun?.urunAdi || d?.icerik?.icerikAdi || d?.icerik?.urunAdi || d?.urunAdi || d?.icerikAdi || 'Cihaz';
                          return d ? <div>Cihaz: {safeText(ad)}{d?.seriNumarasi ? ` (S/N: ${safeText(d.seriNumarasi)})` : ''}</div> : null;
                        })() : null}
                      </div>
                    </div>
                  )}
                  {complaintNote && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Açıklama:</strong>
                      <div style={{ marginTop: '4px', fontSize: '14px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                        {complaintNote}
                      </div>
                    </div>
                  )}
                </>
              )}
              {selectedOperation === 'Arıza' && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Arızalar:</strong> {selectedFaults.length > 0 ? (
                      <div style={{ marginTop: '4px' }}>
                        {selectedFaults.map((fault, index) => {
                          const faultId = fault.id || fault._id;
                          const faultName = fault.icerikAdi || fault.arizaAdi || fault.ad || fault.kategoriAdi || '—';
                          return (
                            <div key={faultId} style={{ fontSize: '14px', color: 'var(--text-dark)', marginBottom: index < selectedFaults.length - 1 ? '4px' : '0' }}>
                              • {faultName}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ fontSize: '14px', color: 'var(--text-gray)', fontStyle: 'italic' }}>Arıza seçilmedi</span>
                    )}
                  </div>
                  {faultNote && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Arıza Notu:</strong>
                      <div style={{ marginTop: '4px', fontSize: '14px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                        {faultNote}
                      </div>
                    </div>
                  )}
                </>
              )}
              {!isIstekSikayet && selectedOperation === 'Kurulum' && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Seçilen Ürün:</strong>{' '}
                    {kurulumIcerikId ? (() => {
                      const u = (kurulumUrunler || []).find((x) => String(x.id || x._id) === String(kurulumIcerikId));
                      const ad = u?.icerikAdi || u?.urunAdi || '';
                      const kod = u?.icerikKodu || u?.urunKodu || '';
                      return (
                        <span style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                          {safeText([ad, kod ? `(${kod})` : ''].filter(Boolean).join(' '))}
                        </span>
                      );
                    })() : (
                      <span style={{ fontSize: '14px', color: 'var(--text-gray)', fontStyle: 'italic' }}>Seçilmedi</span>
                    )}
                  </div>
                  {kurulumSeriNo && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Seri No:</strong>{' '}
                      <span style={{ fontSize: '14px', color: 'var(--text-dark)' }}>{safeText(kurulumSeriNo)}</span>
                    </div>
                  )}
                  {cagriMerkeziSorular.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Kontrol Listesi:</strong>
                      <div style={{ marginTop: '4px' }}>
                        {cagriMerkeziSorular.map((s) => {
                          const sid = String(s._id || s.id);
                          const cevap = checklistCevaplari[sid];
                          const deger = (checklistDegerleri[sid] || '').trim();
                          return (
                            <div key={sid} style={{ fontSize: '14px', color: 'var(--text-dark)', marginBottom: '4px' }}>
                              • {safeText(s.metin || '—')}: {cevap === 'evet' ? 'Evet' : cevap === 'hayir' ? 'Hayır' : '—'}
                              {cevap === 'evet' && deger ? ` (${safeText(deger)}${s.degerBirimi ? ` ${safeText(s.degerBirimi)}` : ''})` : ''}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
              {!isIstekSikayet && selectedOperation !== 'Kurulum' && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Seçilen Cihazlar:</strong> {deviceNotSelected ? (
                  <span>Cihaz seçilmedi</span>
                ) : selectedDevices.length > 0 ? (
                  <div style={{ marginTop: '4px' }}>
                    {selectedDevices.map((deviceId, index) => {
                      const device = devices.find(d => (d.id || d._id) === deviceId);
                      if (!device) return null;
                      // Cihaz adını bul - ürün bilgisinden
                      const deviceName = device.urun?.icerikAdi || device.urun?.urunAdi || 
                                       device.icerik?.icerikAdi || device.icerik?.urunAdi ||
                                       device.urunAdi || device.icerikAdi || 
                                       'Cihaz adı bulunamadı';
                      return (
                        <div key={deviceId} style={{ fontSize: '14px', color: 'var(--text-dark)', marginBottom: index < selectedDevices.length - 1 ? '4px' : '0' }}>
                          • {deviceName}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span>—</span>
                )}
              </div>
              )}
              {!isIstekSikayet && deviceExplanation && (
                <div style={{ marginBottom: '12px' }}>
                  <strong>Cihaz Notu:</strong>
                  <div style={{ marginTop: '4px', fontSize: '14px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                    {deviceExplanation}
                  </div>
                </div>
              )}
              {!isIstekSikayet && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Adres:</strong> {selectedAddress ? (
                  <>
                    <div style={{ marginTop: '4px' }}>{selectedAddress.adres || '—'}</div>
                    {(selectedAddress.city || selectedAddress.district || selectedAddress.cityId || selectedAddress.districtId) && (
                      <div style={{ fontSize: '13px', color: 'var(--text-gray)', marginTop: '4px', fontStyle: 'italic' }}>
                        {(() => {
                          // İl ve ilçe bilgilerini göster - önce populate edilmiş city/district, sonra ID'lerden
                          // Tüm il listelerini birleştir
                          const allIller = [...iller, ...serviceAddressIller];
                          const allIlceler = [...ilceler, ...serviceAddressIlceler];
                          
                          const ilAd = selectedAddress.city?.name || selectedAddress.city?.ad || 
                                       (selectedAddress.cityId && allIller.find(i => String(i.id || i._id) === String(selectedAddress.cityId))?.name) ||
                                       (selectedAddress.cityId && allIller.find(i => String(i.id || i._id) === String(selectedAddress.cityId))?.ad) || '';
                          const ilceAd = selectedAddress.district?.name || selectedAddress.district?.ad || 
                                         (selectedAddress.districtId && allIlceler.find(i => String(i.id || i._id) === String(selectedAddress.districtId))?.name) ||
                                         (selectedAddress.districtId && allIlceler.find(i => String(i.id || i._id) === String(selectedAddress.districtId))?.ad) || '';
                          
                          if (ilAd && ilceAd) {
                            return `${ilceAd} / ${ilAd}`;
                          } else if (ilAd) {
                            return ilAd;
                          } else if (ilceAd) {
                            return ilceAd;
                          }
                          return '';
                        })()}
                      </div>
                    )}
                  </>
                ) : (
                  <span>—</span>
                )}
              </div>
              )}
              {!isIstekSikayet && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Teknik Servis:</strong> {selectedTechServices.length > 0 ? (
                  <span style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                    {selectedTechServices[0].ad || selectedTechServices[0].name || 'Teknik Servis'}
                    {selectedTechServices[0].kod && ` (${selectedTechServices[0].kod})`}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-gray)', fontStyle: 'italic' }}>Seçilmedi</span>
                )}
              </div>
              )}
              {!isIstekSikayet && (
                <div style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-gray)', fontStyle: 'italic' }}>
                  * Randevu ve teknik servis ataması Servis Kaydı Takibi sayfasından yapılacaktır.
                </div>
              )}
            </div>
            
            {!isIstekSikayet ? (
              <div style={{
                background: 'var(--bg-white, #fff)',
                border: '1px solid var(--border-color, #e5e7eb)',
                borderRadius: '10px',
                padding: '16px 20px',
                marginBottom: '24px',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--text-dark)' }}>
                  OTP SMS
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
                  <input
                    type="checkbox"
                    checked={otpVarsayilanNumara}
                    onChange={(e) => {
                      setOtpVarsayilanNumara(e.target.checked);
                      if (e.target.checked) setOtpAlternatifTelefon('');
                    }}
                  />
                  <span>Varsayılan numara</span>
                </label>
                {otpVarsayilanNumara ? (
                  <div style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                    {formatTelefonGoster(selectedCustomer?.telefon || selectedCustomer?.phone || '')}
                  </div>
                ) : (
                  <label className="form-field" style={{ marginBottom: 0 }}>
                    <span>Yetkili telefon numarası *</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={10}
                      placeholder="5XXXXXXXXX"
                      value={otpAlternatifTelefon}
                      onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setOtpAlternatifTelefon(next);
                      }}
                    />
                    <span style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '6px', display: 'block' }}>
                      Başında 0 olmadan tam 10 hane
                    </span>
                  </label>
                )}
              </div>
            ) : null}

            <div style={{ marginBottom: '24px' }}>
              <label className="form-field">
                <span>Önem Derecesi *</span>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  required
                >
                  {PRIORITY_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label className="form-field">
                <span>Notlar</span>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="5"
                  maxLength={1000}
                  placeholder="Servis kaydı ile ilgili notlarınızı buraya yazabilirsiniz..."
                />
              </label>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setActiveTab(isIstekSikayet ? 'complaint-note' : 'tech-service-select');
                }}
                disabled={finalSubmitting}
              >
                <i className="fas fa-arrow-left"></i>
                <span>Geri</span>
              </button>
              <button
                type="button"
                className="primary-btn"
                aria-label="Servis kaydını oluştur"
                onClick={handleCreateServiceRecord}
                disabled={
                  !priority ||
                  finalSubmitting ||
                  (isIstekSikayet
                    ? !complaintCategory || !complaintNote.trim() || !istekSikayetTuru
                    : selectedTechServices.length === 0)
                }
              >
                {finalSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Oluşturuluyor...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    <span>{isIstekSikayet ? 'İstek/Şikayet Oluştur' : 'Servis Kaydını Oluştur'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Arıza Seçim Modal */}
      {isFaultSearchModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setIsFaultSearchModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h2>Arıza Seçimi</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => {
                  setIsFaultSearchModalOpen(false);
                  setModalSelectedFaults([]);
                }}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {faultsLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">Arıza kategorileri yükleniyor...</div>
                </div>
              ) : faultCategories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-gray)' }}>
                  Arıza kategorisi bulunamadı
                </div>
              ) : (
                <div style={{ padding: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 12,
                      flexWrap: 'wrap',
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 13, color: 'var(--text-gray)' }}>
                      Kategorileri açıp arızaları seçin
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={expandAllFaultAccordions}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: 12,
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #d1d5db',
                          background: '#fff',
                          color: '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        <i className="fas fa-folder-open" style={{ marginRight: 6, fontSize: 13 }} aria-hidden />
                        Tümünü aç
                      </button>
                      <button
                        type="button"
                        onClick={collapseAllFaultAccordions}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: 12,
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #d1d5db',
                          background: '#fff',
                          color: '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        <i className="fas fa-folder" style={{ marginRight: 6, fontSize: 13 }} aria-hidden />
                        Tümünü kapat
                      </button>
                    </div>
                  </div>
                  {faultCategories.map((node) => renderModalFaultTree(node))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setIsFaultSearchModalOpen(false);
                  setModalSelectedFaults([]);
                }}
              >
                İptal
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleAddFaultsFromModal}
                disabled={modalSelectedFaults.length === 0}
                style={{
                  opacity: modalSelectedFaults.length === 0 ? 0.5 : 1,
                  cursor: modalSelectedFaults.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                <i className="fas fa-plus"></i>
                <span>Arızaları Ekle ({modalSelectedFaults.length})</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Adres Ekleme Modal */}
      {isAddressModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setIsAddressModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Adres Ekle</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsAddressModalOpen(false)}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <label className="form-field">
                <span>Adres *</span>
                <textarea 
                  value={newAddressText}
                  onChange={(e) => setNewAddressText(e.target.value)}
                  rows="4"
                  maxLength={500}
                  required
                  placeholder="Adres bilgilerini giriniz..."
                />
              </label>
              <label className="form-field">
                <span>İl *</span>
                <select
                  value={newAddressCityId}
                  onChange={(e) => {
                    setNewAddressCityId(e.target.value);
                    setNewAddressDistrictId(''); // İl değiştiğinde ilçeyi sıfırla
                  }}
                  required
                  disabled={serviceAddressIllerLoading}
                >
                  <option value="">İl Seçiniz</option>
                  {serviceAddressIllerLoading ? (
                    <option disabled>İller yükleniyor...</option>
                  ) : serviceAddressIller.length === 0 ? (
                    <option disabled>İl bulunamadı</option>
                  ) : (
                    serviceAddressIller.map((il) => (
                      <option key={il.id} value={String(il.id)}>
                        {il.name || il.ad}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <label className="form-field">
                <span>İlçe *</span>
                <select
                  value={newAddressDistrictId}
                  onChange={(e) => setNewAddressDistrictId(e.target.value)}
                  required
                  disabled={!newAddressCityId || serviceAddressIlcelerLoading}
                >
                  <option value="">İlçe Seçiniz</option>
                  {serviceAddressIlcelerLoading ? (
                    <option disabled>İlçeler yükleniyor...</option>
                  ) : !newAddressCityId ? (
                    <option disabled>Önce il seçiniz</option>
                  ) : serviceAddressIlceler.length === 0 ? (
                    <option disabled>İlçe bulunamadı</option>
                  ) : (
                    serviceAddressIlceler.map((ilce) => (
                      <option key={ilce.id} value={String(ilce.id)}>
                        {ilce.name || ilce.ad}
                      </option>
                    ))
                  )}
                </select>
              </label>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setIsAddressModalOpen(false);
                  setNewAddressText('');
                  setNewAddressCityId('');
                  setNewAddressDistrictId('');
                }}
                disabled={addressSubmitting}
              >
                İptal
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleAddNewAddress}
                disabled={!newAddressText.trim() || !newAddressCityId || !newAddressDistrictId || addressSubmitting}
              >
                {addressSubmitting ? 'Ekleniyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Görüşme Sonlandırma Modalı */}
      {gorusmeSonlandirModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }} onClick={(e) => { if (e.target === e.currentTarget) setGorusmeSonlandirModalOpen(false); }}>
          <div style={{
            background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', gap: '12px',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            }}>
              <i className="fas fa-phone-slash" style={{ fontSize: '20px', color: '#dc2626' }}></i>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#991b1b', fontWeight: 600 }}>
                Görüşmeyi Sonlandır
              </h3>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                  Telefon Numarası
                </label>
                <input
                  type="text"
                  value={gorusmeSonlandirForm.telefon}
                  onChange={(e) => setGorusmeSonlandirForm(prev => ({ ...prev, telefon: e.target.value }))}
                  placeholder="5XX XXX XX XX"
                  readOnly={!!selectedCustomer}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid #d1d5db', fontSize: '14px', outline: 'none',
                    background: selectedCustomer ? '#f3f4f6' : '#fff',
                    boxSizing: 'border-box',
                  }}
                />
                {selectedCustomer && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                    <i className="fas fa-user" style={{ marginRight: '4px' }}></i>
                    {safeText(selectedCustomer.adi || selectedCustomer.name)} {safeText(selectedCustomer.soyadi || selectedCustomer.surname)}
                  </p>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                  Sonlanma Sebebi <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={gorusmeSonlandirForm.sonlanmaSebebi}
                  onChange={(e) => setGorusmeSonlandirForm(prev => ({ ...prev, sonlanmaSebebi: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid #d1d5db', fontSize: '14px', outline: 'none',
                    background: '#fff', boxSizing: 'border-box',
                  }}
                >
                  <option value="">Seçiniz...</option>
                  <option value="İstek">İstek</option>
                  <option value="Şikayet">Şikayet</option>
                  <option value="Bilgi">Bilgi</option>
                  <option value="Bağlantı Problemi">Bağlantı Problemi</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                  Görüşme Notu
                </label>
                <textarea
                  value={gorusmeSonlandirForm.notlar}
                  onChange={(e) => setGorusmeSonlandirForm(prev => ({ ...prev, notlar: e.target.value }))}
                  placeholder="Görüşme ile ilgili notlarınızı yazınız..."
                  rows={4}
                  maxLength={2000}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid #d1d5db', fontSize: '14px', outline: 'none',
                    resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f9fafb',
            }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setGorusmeSonlandirModalOpen(false)}
                disabled={gorusmeSonlandirLoading}
                style={{ padding: '10px 20px', borderRadius: '8px' }}
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleGorusmeSonlandirSubmit}
                disabled={gorusmeSonlandirLoading || !gorusmeSonlandirForm.sonlanmaSebebi}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  background: gorusmeSonlandirLoading || !gorusmeSonlandirForm.sonlanmaSebebi ? '#9ca3af' : '#dc2626',
                  color: '#fff', fontWeight: 500, cursor: gorusmeSonlandirLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}
              >
                {gorusmeSonlandirLoading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Kaydediliyor...</>
                ) : (
                  <><i className="fas fa-phone-slash"></i> Sonlandır ve Kaydet</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teknik Servis Bilgi Modali (İstek ve Şikayet alt-akışı) */}
      {openTechServiceInfoId && (
        <TeknikServisInfoModal
          teknikServisId={openTechServiceInfoId}
          onClose={() => setOpenTechServiceInfoId(null)}
        />
      )}

      {/* Müşteri Servis Kaydı Tabanlı Detay Modali (İstek ve Şikayet alt-akışı) */}
      {openMusteriServisKaydiId && (
        <MusteriServisKaydiTabModal
          servisKaydiId={openMusteriServisKaydiId}
          onClose={() => setOpenMusteriServisKaydiId(null)}
        />
      )}

      <MusteriIzinDogrulamaModal
        isOpen={izinModalOpen}
        musteri={selectedCustomer}
        izinBaglam={{
          gorusmeKaydiId: gorusmeKaydiId || null,
          servisKaydiId: null,
        }}
        onClose={() => setIzinModalOpen(false)}
        onSuccess={(musteri) => {
          if (musteri) {
            setSelectedCustomer((prev) => ({
              ...prev,
              ...musteri,
              id: musteri.id || prev?.id || prev?._id,
              _id: musteri.id || musteri._id || prev?._id || prev?.id,
              iletisimIzni: musteri.iletisimIzni || { verildi: true },
            }));
          } else {
            setSelectedCustomer((prev) => ({
              ...prev,
              iletisimIzni: { verildi: true },
            }));
          }
          setIzinModalOpen(false);
          if (window.showNotification) {
            window.showNotification('success', 'İletişim izni doğrulandı');
          }
        }}
      />
    </main>
  );
};

export default ServiceRecordCreatePage;

